/**
 * TemplateEditor — opens a template for REWORK (Templates → click a row).
 *
 * The product owner's flow: clicking a template opens it in an edit form where you
 * reconfigure (name), reset, and RE-MASK before deciding whether to use it for a
 * batch. Opening/editing here NEVER routes to Batch — only the explicit
 * "Use this template for batch" action does (it adds to the selection store).
 *
 * Re-editing reuses the EXISTING flows:
 *  - "Open editable draft" calls the version flow (`createVersion`) → a fresh Draft
 *    seeded with the current version's masks, edited in the EXISTING MaskEditor, then
 *    persisted with `submitVersion`. With the global approval gate OFF (default) the
 *    submit auto-activates (Draft → Active); with it ON it goes Draft → Pending for
 *    review. An Active version is locked, so a new Draft is the canonical way to
 *    rework it (002 editor-approval).
 *  - "Reset" reverts the working masks to the version they were loaded from.
 *
 * Rendering/CV stays client-side; only createVersion/submit hit the cloud (relay
 * rules unchanged). Offline/demo degrade gracefully: the editor still opens against
 * whatever geometry the selection carries, and cloud calls surface a readable error.
 */
import { confirmAction } from "../ui/confirmAction.js";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useServices } from "../app/context.js";
import { useSessionStore } from "../stores/session.js";
import { customerErrorText, errorDetail } from "../generate/errorCode.js";
import { ErrorNotice } from "../ui/ErrorNotice.js";
import { useSelectionStore, selectionFromTemplate } from "../stores/selection.js";
import { loadTemplateImageBytes, loadTemplateImageUrl } from "../stores/imageStore.js";
import { isDurableRef } from "../app/refs.js";
import { MaskEditor, defaultBrandRect, type MaskEditorProps, type MaskBakeHandle } from "../editor/MaskEditor.js";
import { CameraShadingFix } from "../editor/controls/CameraShadingFix.js";
import { DEFAULT_ISLAND_MARGIN } from "../editor/cameraShading.js";
import { shadingSourceJob, useFinishedCaseUrl } from "./useShadingFix.js";
import { SourcesDialog, type SourceImage } from "./SourcesDialog.js";
import { BASE_TYPES } from "../editor/baseType.js";
import { InlineCreateRow } from "../ui/InlineCreateRow.js";
import { loadCatalogList } from "./catalogOptions.js";
import { useProductCategories } from "./useProductCategories.js";
import { InlineRetry } from "../ui/InlineRetry.js";
import { CatalogSelect } from "../ui/CatalogSelect.js";
import { BusyButton } from "../ui/BusyButton.js";
import { ActionBar } from "../ui/ActionBar.js";
import { Accordion } from "../ui/Accordion.js";
import { Icon } from "../ui/Icon.js";
import { InfoHint, Tooltip } from "../ui/Tooltip.js";
import { RowAction } from "../ui/RowAction.js";
import { ViewPicker } from "../generate/ViewPicker.js";
import { ClearReview } from "../generate/ClearReview.js";
import { fetchResultBytes, isReady, statusBadge, statusLabel, useGenerationJobs } from "../generate/candidates.js";
import { hydrateBakedAssets, ioHydrateDeps, mergeViewRefs, refsForView, dropViewRefs, hasAnyBakedView, type BakedAssetRefsMap } from "./bakedSync.js";
import { withTimeout, fetchWithTimeout, UPLOAD_TIMEOUT_MS } from "./timeout.js";
import { hasMockup, deleteMockup } from "../stores/mockupStore.js";
import { ALL_VIEWS, VIEW_LABEL, PRIMARY_VIEW } from "./views.js";
import type { GeneratedImage } from "../stores/wizard.js";
import { AUTOKEY_TIMEOUT_MS } from "../wizard/Wizard.js";
import type { AutoKeyVerdict } from "../editor/maskEditorTypes.js";

/**
 * The "+ Add view" select is a MENU, not a field: it has no value of its own and snaps back
 * after every pick. Holding it at a constant value is what makes it controlled — the old code
 * wrote `e.target.value = ""` straight to the DOM after calling addView, which React never
 * saw, so a later render could disagree with the box on screen (AUD-2 IM-7).
 */
const ADD_VIEW_PLACEHOLDER = "";
import { syncBakedAssetsToCloud, HostingDisabledError, hostingEnabled } from "./bakedSyncCloud.js";
import { canAccessAdmin } from "../admin/gating.js";
import { refsToInitialLayerRefs } from "./layerRefs.js";
import { VersionHistoryPanel } from "./VersionHistoryPanel.js";
import {
  backgroundRgbaToHex, brandColorPrimary, resolveBackgroundColor, type BackgroundRgba,
} from "./backgroundColor.js";
import { CommentsThread } from "./CommentsThread.js";
import type {
  ProductTemplate,
  TemplateOwnership,
  TemplateVisibility,
  TemplateOwnershipType,
  TemplateSyncPermission,
  TemplateViewType,
  PhoneModelOption,
  CaseStyleOption,
  ImageAsset,
  TemplateStatus,
  ResolvedOptionGroup,
  GenerationJobView,
} from "@genz/platform";
import { HttpError } from "@genz/platform";
import { saveStatusCopy } from "./saveStatusCopy.js";
// WS-B B-2: the shared label + error rules. `statusLabel` is already taken in this file by the
// GENERATION-job one from `generate/candidates.js`, so the template ones are aliased rather
// than renamed — the two vocabularies are genuinely different and both are used here.
import {
  modelLabel,
  statusBadgeClass as templateStatusBadgeClass,
  statusLabel as templateStatusLabel,
} from "./templateLabels.js";
import { templateErrorMessage } from "./templateErrors.js";
// WS-B B-8 / F-40: `display.model` and `display.style` are SERVER-resolved on every list row,
// so a catalog PATCH from in here changes them for every template that references the entry.
// The only correct client response is to tell the list to re-ask.
import { notifyCatalogChanged } from "./catalogInvalidation.js";
// LB-1: the view⇄wire serializer used to live here AND in Wizard.tsx, and the two had drifted.
// One module now owns it, plus the publish guard that names an unmasked view before the cloud
// 422s and rolls every view back.
import {
  fromWirePolygon,
  geometryFromView,
  serverViewErrorMessage,
  stashViewInto,
  toWireMasks,
  validateViewsForPublish,
  viewProblemsMessage,
} from "./wireMasks.js";
// WS-EXIF: the frame a view's 0..1 geometry was drawn on, so a reopened template never draws a
// quad that belongs to a differently-shaped photo.
import { authoringFrameOf, frameVerdict, measureFrame, pinOutgoingFrame } from "./authoringFrame.js";
import { composeBackgroundColor } from "@genz/engine-bind";
import type { Mask, Polygon, WireVersionPayload, WireView } from "@genz/engine-bind";
import { geometry } from "@genz/engine-bind";
// F-5 — the top-of-step geometry banner, with the one-click repair on it.
import { GeometryProblemBanner } from "../editor/GeometryProblemBanner.js";
import "./template-editor.css";

/** The head's trailing summary of a closed section's current values. Plain text: `Accordion`
 *  renders `meta` inside the `<summary>`, so anything interactive here would toggle the
 *  section it describes. */
function sectionSummary(text: string) {
  return <span className="tpl-editor__section-summary">{text}</span>;
}

/** "3 versions" / "1 comment" / "…" while the panel's first fetch is in flight. */
function countSummary(n: number | null, noun: string): string {
  if (n === null) return "…";
  return n === 1 ? `1 ${noun}` : `${n} ${noun}s`;
}

/** A Generate run's view joining this template: added as a view, or replacing that view's case image. */
export interface RunViewIntoTemplate {
  view: string;
  jobId: string;
  image: GeneratedImage;
  clear?: Uint8Array;
}

/** A run view the draft holds: its job, the hosted print guide, and what keying it needs. */
interface TakenRunView {
  jobId: string;
  url: string;
  clear?: Uint8Array;
  productHeightPct?: number | null;
}

export interface TemplateEditorProps {
  /** The list row that was clicked; the full template is re-fetched for its payload. */
  template: ProductTemplate;
  /** Return to the templates list (re-fetches so any new draft shows up). */
  onClose: () => void;
  /** Generate run views this editor opens to take into the template's new Draft. */
  runViews?: RunViewIntoTemplate[];
  /** A run view's job, marked published with this template once a Save submitted it. */
  onGenerationPublished?: (job: GenerationJobView) => void;
}

type SaveState = "idle" | "saving" | "saved";

/** Safety cap on how long Save waits for an in-flight baked-asset sync before submitting anyway.
 *  Serializing Save behind the sync kills the spurious 409 (submitVersion presenting a token the
 *  in-flight `setBakedAssets` is about to rotate), but a wedged upload must NEVER hang Save — on
 *  timeout we fall back to the old racy-but-live behaviour with a warn log. */
const SAVE_SYNC_TIMEOUT_MS = 30_000;

/** ED-18 — cap on the baked-layer re-download a Refresh / Force sync kicks off. Without one a
 *  wedged R2 connection left the button on "Loading…" forever with no way out but a restart. */
const HYDRATE_TIMEOUT_MS = 60_000;

/** Cross-device baked-asset upload status. `submitVersion` (Save) is INDEPENDENT of this —
 *  the asset sync is best-effort and must never block/fail a Save; it only drives the retry
 *  banner so a swallowed upload failure is visible instead of silently rendering design-only
 *  on other devices.
 *
 *  WS-P adds `blocked`: distinct from `failed` because the two need different offers. `failed`
 *  is an upload that MIGHT work next time (a blip, a CORS rule, an outage) — Retry, and Dismiss
 *  if the operator has decided to live with it. `blocked` is a workspace where the upload
 *  CANNOT succeed until a platform admin changes a setting, so dismissing it would be the
 *  operator agreeing to forget the one fact they need. */
type BakedSyncState = "idle" | "syncing" | "ok" | "failed" | "blocked";

/** The exact payload MaskEditor hands to `onBaked` — retained after a failed upload so the
 *  banner's Retry can re-run the same sync with the same bytes. */
type BakedPayload = Parameters<NonNullable<MaskEditorProps["onBaked"]>>[0];

/** P2-3 — the view's brand-logo placement quad as an editor polygon, or null when unset.
 *  Only a real 4-corner quad counts (the engine's `brandQuadForView` applies the same rule). */
function brandAreaFromView(view?: WireView): Polygon | null {
  const q = view?.brand_logo_placement;
  return q && q.length === 4 ? fromWirePolygon(q) : null;
}

/** Pull the primary (back, else first) view out of a payload, for field-preserving submits. */
/** A view's print guide finish, comparable across the draft and the saved payload. */
function finishKey(view: WireView | undefined): string {
  return JSON.stringify([view?.shading_fix ?? null, Boolean(view?.flatten_magenta)]);
}

/** How long Save waits for the finished case image before it bakes anyway. */
const SHADING_LOAD_MS = 10_000;

/** Resolves once `ready()` holds, or after `ms`. */
async function waitFor(ready: () => boolean, ms: number): Promise<void> {
  for (const end = Date.now() + ms; !ready() && Date.now() < end;) {
    await new Promise((r) => setTimeout(r, 100));
  }
}

function primaryView(payload?: WireVersionPayload): WireView | undefined {
  return payload?.views?.find((v) => v.view_type === "back") ?? payload?.views?.[0];
}

/** Find a payload's view by view_type. */
function viewOf(payload: WireVersionPayload | undefined, viewType: string): WireView | undefined {
  return payload?.views?.find((v) => v.view_type === viewType);
}

/** The editable per-view drafts seeded from a version payload. A real payload always carries at
 *  least one view; a (defensive) empty payload defaults to a single back view. Each view is COPIED
 *  so editing a draft never mutates the loaded payload. */
function initialViewDrafts(payload?: WireVersionPayload): WireView[] {
  const views = payload?.views;
  if (views && views.length > 0) return views.map((v) => ({ ...v }));
  return [{ view_type: "back", perspective_points: [], masks: [], layers: [] }];
}

/**
 * WS-B B-2 — the editor's failure funnel, on top of the SHARED one.
 *
 * The local `saveErrorMessage` is gone. It and `templateErrors.templateErrorMessage` were
 * the same function written twice, except that this copy handled only 409 /
 * 422-with-`detail.views` / 403 and then fell through to `e.message` — which for an
 * `HttpError` is the REQUEST LINE ("PATCH /api/templates/ef786a67-…/versions/2 -> 500"), an
 * internal UUID and an HTTP verb painted into the page. 500 was never one of its four cases.
 * `templateErrorMessage` handles all of them PLUS network failures, 401, a plain-string
 * `detail`, and it refuses to print a FastAPI schema-validation list — and it names the
 * ACTION that failed, which the local copy could not.
 *
 * The ONE thing that does not delegate is the 422 geometry sentence, and deliberately:
 * `wireMasks.serverViewErrorMessage` resolves the view through `VIEW_LABEL` (the editor's
 * own tab names) and names the exact validation codes, and its closing clause is written for
 * someone who is ALREADY in the editor — "…— draw it or remove the view."
 * `templateErrors.geometryMessage` is the LIST surface's wording ("…— open the template and
 * draw or remove that view."), which reads as nonsense to an operator standing in the
 * template it is telling them to open. Two surfaces, two correct sentences; collapsing them
 * would both weaken `template-editor-publish-views.test.tsx`'s assertion (IM-1) and lose the
 * per-code detail. So the in-editor wording wins here and the shared funnel handles the rest.
 */
function editorErrorMessage(e: unknown, action: string): string {
  if (e instanceof HttpError && e.status === 422) {
    const named = serverViewErrorMessage(e.body);
    if (named) return named;
  }
  return templateErrorMessage(e, action);
}

/**
 * The image the editor keys (owner, 2026-10-14: the controls changed "the existing one, not the
 * original image"). The list's record carries no case image reference, so before the full record
 * is read the only candidates are the baked copies, which hold no magenta: a print guide template
 * keyed one of those, found nothing, kept its old saved key and never keyed the original after.
 * So nothing is chosen until the full record has been read.
 */
export function resolveCaseImageUrl(o: {
  staged?: string; payloadRef?: string; recordLoaded: boolean;
  localUrl: string | null | undefined; bakedCaseUrl?: string;
}): string | undefined {
  if (o.staged ?? o.payloadRef) return o.staged ?? o.payloadRef;
  if (!o.recordLoaded || o.localUrl === undefined) return undefined;
  return o.localUrl ?? o.bakedCaseUrl;
}

export function TemplateEditor({ template, onClose, runViews, onGenerationPublished }: TemplateEditorProps) {
  const { client, engine, logger, io } = useServices();
  const addToBatch = useSelectionStore((s) => s.addToBatch);
  const inBatch = useSelectionStore((s) =>
    s.batchSelections.some((b) => b.templateId === template.templateId),
  );

  // Re-fetch the full template so the version PAYLOAD (real geometry) is present;
  // the list row carries only summary fields. Fall back to the row on failure.
  const [full, setFull] = useState<ProductTemplate>(template);
  /** The full record has been read once (or could not be). See `resolveCaseImageUrl`. */
  const [recordLoaded, setRecordLoaded] = useState(false);
  const [name, setName] = useState(template.name);
  /* Which of the four panels is open, held HERE and not inside `Accordion` (spec §6, rule 8's
     controlled pair): `defaultOpen` is a seed read once at mount, and a page that owns the key
     keeps the open panel across anything that re-seats the primitive. IMP-9 is the seed value —
     Details is the field set an operator checks first. */
  const [sectionOpen, setSectionOpen] = useState<string | null>("details");
  /* …and which section of the MASK rail is open (fix round 1, F-5). It lives HERE, not in
     `MaskEditor`, because this page renders that component under a `key` built from
     `forceSyncEpoch` and `prunedEpoch` — a forced re-download or a view tombstone remounts it,
     and state inside it goes with the mount. Neither is a reason to shut the rail on the
     operator, and the page is the only thing that outlives the `key` it writes. */
  const [maskRailOpen, setMaskRailOpen] = useState<string | null>("bg-removal");

  // Multi-view authoring (Task B4). `viewDrafts` is the source of truth for EVERY view's geometry
  // + reference; `activeView` is the tab currently on the canvas. `printArea`/`cutouts` mirror the
  // ACTIVE view's live geometry and are stashed back into its draft on every tab switch + Save, so
  // an untouched sibling view round-trips field-for-field through the full `views[]` submit.
  const [viewDrafts, setViewDrafts] = useState<WireView[]>(() => initialViewDrafts(template.currentVersion.payload));
  const [activeView, setActiveView] = useState<string>(
    () => primaryView(template.currentVersion.payload)?.view_type ?? PRIMARY_VIEW,
  );
  const [printArea, setPrintArea] = useState<Polygon>(
    () => geometryFromView(primaryView(template.currentVersion.payload)).printArea,
  );
  const [cutouts, setCutouts] = useState<Mask[]>(
    () => geometryFromView(primaryView(template.currentVersion.payload)).cutouts,
  );
  // P2-3 — the ACTIVE view's brand-logo placement quad (spec 017 US6). The MaskEditor has rendered
  // a draggable amber box for this all along and the render path already reads
  // `brand_logo_placement`, but the TemplateEditor never passed `brandArea`/`onBrandAreaChange`, so
  // there was NO authoring path outside the wizard: a placement could be created once and then
  // never adjusted. Mirrors `printArea`/`cutouts` — stashed into the view draft on every tab
  // switch + Save, so sibling views round-trip untouched.
  const [brandArea, setBrandArea] = useState<Polygon | null>(
    () => brandAreaFromView(primaryView(template.currentVersion.payload)),
  );
  /** Natural pixel dims of the case image, so a freshly-added brand box is a real 200×100px. */
  const [baseDims, setBaseDims] = useState<{ w: number; h: number } | null>(null);
  // A ref mirror of `activeView` so `refreshFull` can preserve the open tab across a re-fetch
  // WITHOUT taking `activeView` as a dep (that would re-run the refresh effect — and clobber
  // unsaved edits — on every tab switch).
  const activeViewRef = useRef(activeView);
  useEffect(() => { activeViewRef.current = activeView; }, [activeView]);

  // B4: the version's LIVE baked-asset refs map. `full.currentVersion.bakedAssets` is a MOUNT-time
  // snapshot only refreshed by `refreshFull` (which the bake/save paths never re-run), so reading it
  // for the per-view `mergeViewRefs` base would rebuild from the open-time value and CLOBBER a
  // same-session sibling bake (setBakedAssets is a wholesale cloud overwrite). This ref threads the
  // persisted map forward: seeded from the mount snapshot, advanced after every successful
  // baked-asset persist (the exact merged map sent to setBakedAssets) + the removed-view prune, and
  // reset to server truth on refreshFull. It is the base for the merge, matte carry-forward, and
  // cleanup everywhere the old stale snapshot was read.
  const lastKnownRefsRef = useRef<BakedAssetRefsMap | null>(
    (template.currentVersion.bakedAssets as BakedAssetRefsMap | null | undefined) ?? null,
  );
  /**
   * ED-12 — views the admin REMOVED in this session, tombstoned until the next Save prunes them
   * from the cloud. The refs map is only cleaned up on Save, so removing a view and re-adding it
   * before saving used to REVIVE its baked refs: the "blank" new view came back with the deleted
   * view's matte and texture restored onto it. Every read of the refs (the MaskEditor's
   * `initialLayerRefs`, the hosted case-image fallback) treats a tombstoned view as having none,
   * and Save prunes tombstoned views even when they were re-added. Cleared after a successful Save.
   */
  const prunedViewsRef = useRef<Set<string>>(new Set());
  /** Bumped whenever a tombstone is added so the MaskEditor remounts against the pruned refs even
   *  if the active view didn't change. */
  const [prunedEpoch, setPrunedEpoch] = useState(0);
  /** The LIVE per-view baked refs as this device knows them, with tombstoned views removed (ED-12)
   *  and the mount-time snapshot only as a fallback. ED-2: reading `full.currentVersion.bakedAssets`
   *  directly meant a view-tab switch remounted the editor against the OPEN-TIME snapshot, so an AI
   *  removal / calibration done THIS session was lost on the way back. */
  const liveRefs = (): BakedAssetRefsMap | null | undefined => {
    const refs = lastKnownRefsRef.current
      ?? (full.currentVersion.bakedAssets as BakedAssetRefsMap | null | undefined);
    const pruned = prunedViewsRef.current;
    return pruned.size > 0 ? (dropViewRefs(refs, [...pruned]) ?? null) : refs;
  };

  // The editable Draft (002 version flow). Until it's opened the MaskEditor is
  // locked: an Active version is immutable, so reworking means a fresh Draft.
  const [draft, setDraft] = useState<{ versionId: string; versionNumber: number; versionToken: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [save, setSave] = useState<SaveState>("idle");
  // The status the LAST successful save returned (Active when the gate is off → auto-approved,
  // else Pending). The "saved" line derives its copy from this — never from `approvalsEnabled`,
  // which is only a best-effort pre-save guess used for the button verb.
  const [savedStatus, setSavedStatus] = useState<TemplateStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** ED-17 — NON-BLOCKING notices from an otherwise SUCCESSFUL action (e.g. the case-photo
   *  backfill couldn't publish, but the geometry saved fine). These used to reuse `error`, so a
   *  successful Publish rendered a red `role="alert"` banner right next to the green "Published"
   *  chip. Rendered as a warn banner instead, and cleared at the start of every action. */
  const [warning, setWarning] = useState<string | null>(null);
  // Baked-asset cross-device sync (spec 019). A failed upload used to be swallowed (Save still
  // reported success) so other devices rendered the template WITHOUT its background removal and
  // the admin never knew. Track the status + retain the last payload so the banner can retry the
  // exact same upload. Kept SEPARATE from `save`: Save must stay non-blocking / independent.
  const [bakedSync, setBakedSync] = useState<BakedSyncState>("idle");
  /** True while the header Force-sync re-downloads this template's baked layers. */
  const [forceSyncing, setForceSyncing] = useState(false);
  /** Bumped after a forced re-download so the OPEN MaskEditor remounts and re-restores its
   *  calibration/settings/assets from the FRESH record — without this the fresh bytes land
   *  in storage but the mounted editor keeps showing the stale state ("no redownload on
   *  force" report). */
  const [forceSyncEpoch, setForceSyncEpoch] = useState(0);
  const [bakedSyncDismissed, setBakedSyncDismissed] = useState(false);
  const [bakedSyncRetrying, setBakedSyncRetrying] = useState(false);
  /**
   * review-1 m-1: skip the hosting PRE-FLIGHT for the duration of one blocked-banner Retry.
   *
   * `hostingEnabled()` is a module value warmed once per session (App.tsx, keyed
   * `[services.client, session]`). Once it is `false`, nothing re-reads it — so an admin who
   * follows the banner's own instruction ("Turn on hosting in Settings → Hosting."), comes
   * back and presses Retry got the same blocked banner until they signed out and in. The one
   * action the banner offers was inert for exactly the operator who had just fixed it.
   *
   * A latch rather than an argument because the dirty path goes out through `bakeNow` →
   * `onBaked` → `syncBakedAssets`, which the Retry never calls directly. Passing `undefined`
   * hands the verdict back to the presign's own 409 — the already-tested backstop — instead of
   * a stale session value; it never claims hosting is on.
   */
  const skipHostingPreflightRef = useRef(false);
  /** WS-P: who is looking. Decides only whether the hosting-off banner says "Turn on hosting"
   *  or "Ask a platform admin to" — the server is the authority on who may actually do it. */
  const viewerRole = useSessionStore((s) => s.session?.role);
  const isAdminViewer = viewerRole === "tenant_admin" || viewerRole === "super_admin";
  /** A customer taking a Generate run's views gets the canvas, the views and Publish only. */
  const placementOnly = Boolean(runViews?.length) && !canAccessAdmin(viewerRole);
  /**
   * WHY the cross-device sync failed, shown in the banner.
   *
   * The failure was already logged with the host and HTTP status, but the banner said only
   * "the cloud copy didn't sync" — so the one person looking at it had to go to
   * Settings → Observability → Error log to learn anything actionable. A 403 from hosting, a
   * blocked request and a local-hosting misconfiguration need completely different responses;
   * "didn't sync" is the same sentence for all three.
   */
  const [bakedSyncError, setBakedSyncError] = useState<string | null>(null);
  const lastBakedRef = useRef<BakedPayload | null>(null);
  // The in-flight baked-asset sync promise (upload → setBakedAssets → token adoption). Save awaits
  // it before submitVersion so it can't present the pre-rotation token (→ spurious 409). Holds the
  // WHOLE chain incl. adoption; cleared on settle (only if not already replaced by a newer bake).
  const pendingSyncRef = useRef<Promise<void> | null>(null);
  // The freshest draft version token. `setBakedAssets` rotates the token server-side and the sync
  // adopts it asynchronously (setDraft), so after Save awaits the sync the `draft` CLOSURE may still
  // hold the pre-rotation token. This ref is updated at EVERY place the token is adopted/rotated
  // (openDraft, sync adoption, submit) so Save's submit always sends the current token.
  const latestTokenRef = useRef<string | undefined>(undefined);
  // Mirrors the OPEN draft's version number, kept in sync alongside latestTokenRef. Lets the sync's
  // token adoption guard synchronously (a bake resolving after the user opened a NEWER draft must
  // not clobber latestTokenRef) without reading the possibly-stale `draft` closure or a deferred
  // setDraft updater.
  const draftVersionRef = useRef<number | null>(null);
  // A3 — imperative handle into the MaskEditor's bake state. Save reads `isDirty()` and, if a
  // bake-relevant appearance change is pending (AI edge, rotation, fill, texture/clear-bg…),
  // forces `bakeNow()` BEFORE submitVersion so the cloud's baked assets reflect what the admin
  // sees (otherwise Save persists the geometry but other devices render the stale/absent bake).
  const bakeRef = useRef<MaskBakeHandle>(null);
  // Distinct error state for openDraft failures so the "Unlock & edit" retry
  // button can be shown alongside its own contextual error (separate from save
  // errors which appear in the shared `error` banner at the top of the form).
  const [unlockError, setUnlockError] = useState<string | null>(null);
  // Platform-global approval gate. Default OFF (matches the server default and demo);
  // when ON, finishing a template sends it for review instead of activating it.
  const [approvalsEnabled, setApprovalsEnabled] = useState(false);

  // --- Item 5A: ownership / visibility metadata (setTemplateOwnership) ------
  // There is no read endpoint for ownership; the selects default to "keep" and we
  // hydrate them from the PATCH response (the canonical post-save state) so nothing
  // is faked. Each select stays "" (= leave unchanged) until the admin picks a value.
  const [visibility, setVisibility] = useState<TemplateVisibility | "">("");
  const [ownershipType, setOwnershipType] = useState<TemplateOwnershipType | "">("");
  const [syncPermission, setSyncPermission] = useState<TemplateSyncPermission | "">("");
  const [ownerViewType, setOwnerViewType] = useState<TemplateViewType | "">("");
  const [ownerSaving, setOwnerSaving] = useState(false);
  const [ownerSaved, setOwnerSaved] = useState(false);

  // --- Item 5D: replace reference image (templateReferenceUpload presign) ---
  // The newly-uploaded CDN url; threaded onto the next submit so the geometry and
  // the swapped-in reference image persist together.
  // B4: the replaced reference image is now PER VIEW (keyed by view_type), applied on the next
  // save alongside that view's geometry.
  const [newRefUrl, setNewRefUrl] = useState<Record<string, string | undefined>>({});
  const [refUploading, setRefUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // --- Task B5: admin generate parity — per-view AI reference generation ------
  // The Views axis group + the entitlement view cap, resolved from the SAME sources the
  // customer GeneratePanel reads (getResolvedOptions + templateOptions). A best-effort fetch
  // (a failure just hides the whole "Generate views (AI)" section — see the axis-gated render).
  const [viewsAxisGroup, setViewsAxisGroup] = useState<ResolvedOptionGroup | null>(null);
  const [genMaxViews, setGenMaxViews] = useState<number | null>(null);
  // The reference-mock image fed as the generation input (same as GeneratePanel's design upload).
  const [genDesign, setGenDesign] = useState<{ name: string; bytes: Uint8Array; mime?: string } | undefined>(undefined);
  // The views selected for generation (by view_type — the axis choice `value`).
  const [genSelectedViews, setGenSelectedViews] = useState<string[]>([]);
  // The enqueued per-view jobs (id + view), polled to terminal by useGenerationJobs below.
  const [genJobs, setGenJobs] = useState<{ id: string; view: string; job: GenerationJobView }[]>([]);
  /** The generation whose clear image is open for review (null = closed). */
  const [genReviewId, setGenReviewId] = useState<string | null>(null);
  // The last view a generated result was applied-as-reference to (drives a small confirmation line).
  const [genAppliedView, setGenAppliedView] = useState<string | null>(null);

  // Bumped after a save / lifecycle action so the version-history + comments panels re-fetch.
  const [panelRefresh, setPanelRefresh] = useState(0);
  /** WS-Z6 — reported up by the two panels so their accordion heads can show a count
   *  without the page re-listing versions and comments on its own. */
  const [versionCount, setVersionCount] = useState<number | null>(null);
  const [commentCount, setCommentCount] = useState<number | null>(null);

  // --- #4/#6: editable template DETAILS (category / model / style / base type). Seeded
  // from the loaded template; saved via updateTemplateDetails (metadata-only, allowed even
  // on a locked/Active version). Catalog selects are data-driven (best-effort fetch). ---
  const [phoneModels, setPhoneModels] = useState<PhoneModelOption[]>([]);
  const [caseStyles, setCaseStyles] = useState<CaseStyleOption[]>([]);
  const [textureOptions, setTextureOptions] = useState<ImageAsset[]>([]);
  const [clearBgOptions, setClearBgOptions] = useState<ImageAsset[]>([]);
  // A per-list fetch failure is SURFACED (not swallowed): the Model/Style selects show a
  // "couldn't load" option + Retry, and a failed texture/clear-bg fetch shows a retry line
  // above the preview (its selects live inside the MaskEditor). (Task 12 / C4.)
  const [phoneModelsError, setPhoneModelsError] = useState(false);
  const [caseStylesError, setCaseStylesError] = useState(false);
  const [textureError, setTextureError] = useState(false);
  const [clearBgError, setClearBgError] = useState(false);
  const [detailCategory, setDetailCategory] = useState(full.display?.category ?? "");
  const categories = useProductCategories(detailCategory);
  const [detailModel, setDetailModel] = useState(full.phoneModelId ?? "");
  const [detailStyle, setDetailStyle] = useState(full.caseStyleId ?? "");
  const [detailBaseType, setDetailBaseType] = useState(full.baseType ?? "");
  const [detailCodeError, setDetailCodeError] = useState<string | null>(null);
  const detailCodeRef = useRef<HTMLInputElement>(null);
  const [detailCode, setDetailCode] = useState(full.code ?? "");
  const [detailSaving, setDetailSaving] = useState(false);
  const [detailSaved, setDetailSaved] = useState(false);
  // Task 6: per-template background. Seeded from the persisted render_settings.background —
  // absent/null ⇒ "" (no background), and Save leaves render_settings untouched in that case
  // (compat: never inject a default on a legacy template). The picker options come from the
  // same resolved Background option group the customer flow reads (best-effort fetch).
  const [background, setBackground] = useState<string>(
    () => template.currentVersion.payload?.render_settings?.background ?? "",
  );
  const [backgroundGroup, setBackgroundGroup] = useState<ResolvedOptionGroup | null>(null);
  // WS-BG: the COLOUR half of the Background setting. `solid` stores the operator's pick;
  // `brand_color` stores the brand's `color_primary`, re-resolved on every Save. Seeded from
  // the persisted `background_color` so reopening the editor shows the colour that is stored
  // (the RESTORE leg), and defaulted to a neutral so a fresh "Solid color" is never white.
  const [backgroundHex, setBackgroundHex] = useState<string>(
    () => backgroundRgbaToHex(
      template.currentVersion.payload?.render_settings?.background_color as BackgroundRgba | null,
    ),
  );
  /**
   * P0-1 (DATA LOSS) — did the operator actually TOUCH the Background picker this session?
   *
   * The seed above reads the LIST ROW, which carries no `payload` at all (the row is a summary;
   * only `getTemplate` returns the version payload). So on the real screen the picker always
   * mounted at "" / "— None —" while the template genuinely had a background persisted, and
   * Save's `if (background || persistedBackground)` then wrote `background: null` — every
   * Publish silently deleted the per-template background. Two guards, both needed:
   *  1. `persistedBackgroundValue` + the effect below re-seed the picker from the AUTHORITATIVE
   *     payload as soon as `refreshFull` loads it (the picker shows the truth).
   *  2. This ref gates the CLEAR branch in Save: an untouched picker can never write `null` over
   *     a stored value, even if the refetch failed / raced / returned a payload-less template.
   */
  const backgroundTouchedRef = useRef(false);
  /**
   * WS-BG F2 — the brand's primary colour, for the LIVE mock-up preview only.
   *
   * Save re-reads the brand list itself (that read is the system of record; see `saveDraft`).
   * This one exists so choosing "Brand color" SHOWS the brand's colour in the preview instead of
   * white. Best-effort and silent: a failure here costs a preview tint, and the Save path is what
   * reports an unresolvable brand colour to the operator.
   */
  const [previewBrandColor, setPreviewBrandColor] = useState<string | null>(null);
  useEffect(() => {
    if (background !== "brand_color") return;
    let cancelled = false;
    void (async () => {
      try {
        const c = brandColorPrimary(await client.listBrands());
        if (!cancelled) setPreviewBrandColor(c);
      } catch { /* preview-only — Save reports a brand colour it cannot resolve */ }
    })();
    return () => { cancelled = true; };
  }, [background, client]);
  /**
   * The canvas the live preview composites on, from the LIVE picker + swatch — not from the
   * persisted payload, so the operator sees the change as they make it. Same two decisions the
   * export makes, through the same function (`composeBackgroundColor`); the preview is encoded as
   * PNG, so that is the format it is asked about.
   */
  const previewBackgroundColor = useMemo(
    () => composeBackgroundColor(
      background,
      resolveBackgroundColor(background, { solidHex: backgroundHex, brandColor: previewBrandColor }),
      "png",
    ),
    [background, backgroundHex, previewBrandColor],
  );
  const persistedBackgroundValue = full.currentVersion.payload?.render_settings?.background ?? "";
  // WS-BG: the persisted colour, as hex, so the swatch follows the same payload the picker does
  // (the list row carries no payload — see the P0-1 note above).
  const persistedBackgroundHex = backgroundRgbaToHex(
    full.currentVersion.payload?.render_settings?.background_color as BackgroundRgba | null,
  );
  useEffect(() => {
    // Never clobber an in-progress pick — only an UNTOUCHED picker follows the payload.
    if (backgroundTouchedRef.current) return;
    setBackground(persistedBackgroundValue);
    setBackgroundHex(persistedBackgroundHex);
  }, [persistedBackgroundValue, persistedBackgroundHex]);
  // Each catalog list loads independently so one failing (offline / demo / a 500) no longer
  // swallows ALL four into empty selects — a failure flips its error flag and is retryable.
  const loadPhoneModels = useCallback(async () => {
    const r = await loadCatalogList("phone_models", () => client.listPhoneModels(), logger);
    setPhoneModels(r.data);
    setPhoneModelsError(!!r.error);
  }, [client, logger]);
  const loadCaseStyles = useCallback(async () => {
    const r = await loadCatalogList("case_styles", () => client.listCaseStyles(), logger);
    setCaseStyles(r.data);
    setCaseStylesError(!!r.error);
  }, [client, logger]);
  /** Readable message for a failed inline catalog create (403 = not an admin). */
  const catalogCreateError = useCallback((e: unknown, what: string) => {
    setError(e instanceof HttpError && e.status === 403
      ? `Only an admin can add a ${what}.`
      : e instanceof Error ? e.message : String(e));
  }, []);
  /** Details #4/#6 extended — the Model/Style selects are no longer dead ends when the
   *  catalog lacks the value: add it inline (same flow as the creation Wizard). */
  const submitNewModel = useCallback(async (values: Record<string, string>) => {
    const created = await client.createPhoneModel({ brand: values.brand!, model: values.model! });
    notifyCatalogChanged("phoneModel");
    setPhoneModels((prev) => (prev.some((p) => p.id === created.id) ? prev : [...prev, created]));
    setDetailModel(created.id);
    setDetailSaved(false);
  }, [client]);
  const submitNewStyle = useCallback(async (values: Record<string, string>) => {
    const label = values.label!;
    const code = label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || `style-${Date.now()}`;
    const created = await client.createCaseStyle({ code, label });
    notifyCatalogChanged("caseStyle");
    setCaseStyles((prev) => (prev.some((c) => c.id === created.id) ? prev : [...prev, created]));
    setDetailStyle(created.id);
    setDetailSaved(false);
  }, [client]);
  /** Edit the SELECTED catalog entry in place — a typo fix propagates everywhere the
   *  global catalog is shown, without touching the template itself. */
  const submitEditModel = useCallback(async (values: Record<string, string>) => {
    const updated = await client.updatePhoneModel(detailModel, { brand: values.brand!, model: values.model! });
    notifyCatalogChanged("phoneModel");
    setPhoneModels((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  }, [client, detailModel]);
  const submitEditStyle = useCallback(async (values: Record<string, string>) => {
    const updated = await client.updateCaseStyle(detailStyle, { label: values.label! });
    notifyCatalogChanged("caseStyle");
    setCaseStyles((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  }, [client, detailStyle]);
  const loadTextures = useCallback(async () => {
    const r = await loadCatalogList("textures", () => client.listImageAssets("texture"), logger);
    setTextureOptions(r.data);
    setTextureError(!!r.error);
  }, [client, logger]);
  const loadClearBg = useCallback(async () => {
    const r = await loadCatalogList("clear_backgrounds", () => client.listImageAssets("clear_background"), logger);
    setClearBgOptions(r.data);
    setClearBgError(!!r.error);
  }, [client, logger]);
  useEffect(() => {
    void loadPhoneModels();
    void loadCaseStyles();
    void loadTextures();
    void loadClearBg();
  }, [loadPhoneModels, loadCaseStyles, loadTextures, loadClearBg]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const policy = await client.approvalPolicy();
        if (!cancelled) setApprovalsEnabled(policy.approvals_enabled);
      } catch {
        /* offline / demo — keep the default (approvals off) */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client]);

  // Task 6: load the Background option group for the picker (best-effort — a failure just
  // hides the picker; the persisted background value is unaffected either way).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const resolved = await client.getResolvedOptions();
        if (!cancelled) {
          setBackgroundGroup(resolved.groups.find((g) => g.key === "background") ?? null);
          // B5: the Views axis group drives the "Generate views (AI)" picker (same source the
          // customer GeneratePanel reads). Absent ⇒ the whole generate section stays hidden.
          setViewsAxisGroup(resolved.groups.find((g) => g.kind === "axis") ?? null);
        }
      } catch {
        /* best-effort — no group means the picker(s) are hidden */
      }
    })();
    // B5: the entitlement view cap for the generate picker (same source GeneratePanel reads).
    void (async () => {
      try {
        const opts = await client.templateOptions();
        if (!cancelled) setGenMaxViews(opts.max_views ?? null);
      } catch {
        /* cap is best-effort — leave unlimited if unknown */
      }
    })();
    return () => { cancelled = true; };
  }, [client]);

  /**
   * ED-3 — does the editor hold work a re-pull would DESTROY?
   *
   * `refreshFull` rebuilds `viewDrafts` / `printArea` / `cutouts` from the server payload and
   * (with `force`) remounts the MaskEditor, wiping the AI removal, the calibration sliders and
   * every staged layer. Both Refresh and Force sync did that with no warning. "Unsaved" here is:
   * canvas geometry that differs from the loaded payload, a bake-relevant editor change that
   * hasn't been uploaded, or a staged (not-yet-saved) reference image.
   */
  const hasUnsavedWork = useCallback((): boolean => {
    if (bakeRef.current?.isDirty()) return true;
    if (Object.values(newRefUrl).some(Boolean)) return true;
    const persisted = full.currentVersion.payload;
    // The ACTIVE view's canvas geometry vs. what the server holds for it.
    const liveMasks = JSON.stringify(toWireMasks(printArea, cutouts));
    const savedActive = viewOf(persisted, activeView) ?? primaryView(persisted);
    const savedActiveGeo = geometryFromView(savedActive);
    if (liveMasks !== JSON.stringify(toWireMasks(savedActiveGeo.printArea, savedActiveGeo.cutouts))) return true;
    // P2-3: an edited brand-logo box is unsaved work too — a re-pull would silently discard it.
    if (JSON.stringify(brandArea) !== JSON.stringify(brandAreaFromView(savedActive))) return true;
    // Any OTHER view whose draft diverged (added / removed / re-masked on another tab).
    const savedTypes = (persisted?.views ?? []).map((v) => v.view_type).sort();
    const draftTypes = viewDrafts.map((v) => v.view_type).sort();
    if (JSON.stringify(savedTypes) !== JSON.stringify(draftTypes)) return true;
    if (viewDrafts.some((d) => finishKey(d) !== finishKey(viewOf(persisted, d.view_type)))) return true;
    return viewDrafts.some((d) => {
      if (d.view_type === activeView) return false; // covered by the canvas comparison above
      const saved = viewOf(persisted, d.view_type);
      return JSON.stringify(d.masks ?? []) !== JSON.stringify(saved?.masks ?? []);
    });
  }, [newRefUrl, full.currentVersion.payload, printArea, cutouts, brandArea, activeView, viewDrafts]);

  /** ED-3 — ask before a re-pull throws unsaved work away. Returns true to PROCEED. */
  const confirmDiscardUnsaved = useCallback(async (what: string): Promise<boolean> => {
    if (!hasUnsavedWork()) return true;
    const msg = `${what} replaces everything in this editor with the cloud's copy — your unsaved masks, background removal and staged images will be lost. Continue?`;
    return confirmAction(msg);
  }, [hasUnsavedWork]);

  /** Re-fetch the full template (real geometry + current version). Reused after a
   *  rollback/reopen so the editor reflects the new current version. */
  const refreshFull = useCallback(async (opts?: { force?: boolean }) => {
    try {
      const t = await client.getTemplate(template.templateId);
      setRecordLoaded(true);
      if (!t) return;
      setFull(t);
      setName(t.name);
      setDetailCategory(t.display?.category ?? "");
      setDetailModel(t.phoneModelId ?? "");
      setDetailStyle(t.caseStyleId ?? "");
      setDetailBaseType(t.baseType ?? "");
      setDetailCode(t.code ?? "");
      // Rebuild the per-view drafts from the (re-fetched) payload, preserving the OPEN tab when it
      // still exists (else fall back to the primary view). `activeViewRef` avoids taking activeView
      // as a dep of this callback (that would re-run the refresh effect on every tab switch).
      const drafts = initialViewDrafts(t.currentVersion.payload);
      setViewDrafts(drafts);
      const keep = drafts.some((v) => v.view_type === activeViewRef.current)
        ? activeViewRef.current
        : (primaryView(t.currentVersion.payload)?.view_type ?? PRIMARY_VIEW);
      setActiveView(keep);
      const keptView = viewOf(t.currentVersion.payload, keep) ?? primaryView(t.currentVersion.payload);
      const g = geometryFromView(keptView);
      setPrintArea(g.printArea);
      setCutouts(g.cutouts);
      setBrandArea(brandAreaFromView(keptView)); // P2-3: the brand box follows the reloaded view
      // Download the spec-019 baked layered-mockup assets locally so a template built on
      // ANOTHER system composites ALL layers here (render/export) instead of a design-only
      // fallback. Idempotent + best-effort (a fetch failure never blocks editing). B4: hydrate
      // EVERY saved view's refs so reopening a multi-view template restores all views' assets.
      const refs = t.currentVersion?.bakedAssets as BakedAssetRefsMap | null | undefined;
      // Fresh server truth wins — reset the live snapshot the bake/save merges build onto, and
      // drop the local removed-view tombstones (ED-12) since the refetch IS the cloud's answer.
      lastKnownRefsRef.current = refs ?? null;
      prunedViewsRef.current = new Set();
      // ED-17: a successful re-pull clears the banner the LAST failed one raised.
      if (opts?.force) setError(null);
      if (refs && t.currentVersion?.versionId) {
        const views = t.currentVersion.payload?.views?.map((v) => v.view_type) ?? [PRIMARY_VIEW];
        // `force` bypasses the already-local fast path: multi-computer editing means THIS
        // device's copy can be stale minutes after another machine re-bakes — Force sync
        // re-downloads every view even when a local copy exists.
        // ED-18: bounded — a wedged R2 connection used to leave Force sync spinning forever.
        await withTimeout(
          hydrateBakedAssets(t.currentVersion.versionId, refs, ioHydrateDeps(io), { views, force: opts?.force }),
          HYDRATE_TIMEOUT_MS,
          "Baked-layer download",
        )
          .catch((e) => {
            // DON'T swallow this silently: on a device that didn't author the template, a
            // failed download here is exactly "the layers are missing for the other user".
            // The message carries the cause list in likelihood order (see `bakedSync`'s
            // transport-failure text) — the device's own network path first, the bucket CORS
            // policy (docs/deploy-cors.md) last. Editing still works; the operator just needs
            // to know WHY layers are absent.
            const msg = e instanceof Error ? e.message : String(e);
            logger?.log("error", `Editor · baked-layer download failed — layers will be missing on this device (${msg}). Work through the causes named above in order, then Templates → Force sync all; the bucket CORS policy (docs/deploy-cors.md) is the last one to suspect, not the first.`);
            setBakedSync("failed");
          });
      }
    } catch (e) {
      setRecordLoaded(true);
      // Offline / demo — keep the list row's geometry; editing still works locally. But a
      // FORCED refresh is the user explicitly asking "pull the other machine's edits": a
      // swallowed failure here made the Force sync button spin and then show STALE data as
      // if it had synced. Surface that one.
      if (opts?.force) {
        const msg = e instanceof Error ? e.message : String(e);
        logger?.log("error", `Editor · Force sync could not re-fetch the template (${msg}) — showing the LAST LOCAL copy, not the cloud's`);
        setError(`Force sync failed — couldn't re-fetch this template (${msg}). You are still looking at this device's last local copy.`);
      }
    }
  }, [client, io, logger, template.templateId]);

  /**
   * #4/#6 — persist edited details (category / model / style / base type), then refresh from the
   * canonical response. Metadata-only, so it works on a locked/Active template.
   *
   * IM-3: every field is now OMITTED when empty. The old comment here claimed "the selects always
   * hold a concrete value" — they do not: `detailModel` seeds from `full.phoneModelId ?? ""` and
   * `CatalogSelect` renders `<option value="">—</option>`. `patch_template` writes anything that
   * is not None, so a template with no model came back with `phone_model_id = ''` (a dangling
   * reference that behaves differently from NULL in every join and `IS NULL` filter) and a
   * `product_type` of `''`, which drops it out of its own `?product_category=` filtered list.
   *
   * IM-4: omitting is also all the client CAN do — the cloud's `patch_template` treats a JSON
   * `null` as "leave unchanged", so there is no clear-this-field verb on the wire at all. Rather
   * than render "Details saved." beside a field that visibly snaps back on the refetch, an
   * emptied-but-unclearable field raises a warning naming it.
   */
  const saveDetails = useCallback(async () => {
    // ED-3 (missed surface): this is a metadata-only PATCH, but it finishes with `refreshFull()`,
    // which rebuilds `viewDrafts`/`printArea`/`cutouts` from the server payload — so pressing it
    // with unpublished mask/view edits open threw them away with no warning at all (Refresh and
    // Force sync already ask). Same gate, same wording, before anything is written.
    if (!await confirmDiscardUnsaved("Save details")) return;
    setDetailSaving(true);
    setDetailCodeError(null);
    setError(null);
    setWarning(null); // ED-17: a new action clears the previous one's notice
    // Fields the operator emptied that the template still holds a value for — the PATCH cannot
    // express "clear this", so they will come back on the refetch. Named, not silently reverted.
    const unclearable = [
      { label: "Category", emptied: !detailCategory, had: full.display?.category },
      { label: "Model", emptied: !detailModel, had: full.phoneModelId },
      { label: "Style", emptied: !detailStyle, had: full.caseStyleId },
      { label: "Base type", emptied: !detailBaseType, had: full.baseType },
      { label: "Code", emptied: !detailCode.trim(), had: full.code },
    ].filter((f) => f.emptied && f.had).map((f) => f.label);
    try {
      await client.updateTemplateDetails(full.templateId, {
        // Item: the NAME was previously edited in local state only and never sent, so
        // it reverted on reload / never reached another system. Persist it here (the
        // backend PATCH already accepts `name`; name-only is pure metadata, no lock guard).
        name: name.trim() || undefined,
        productType: detailCategory || undefined,
        phoneModelId: detailModel || undefined,
        caseStyleId: detailStyle || undefined,
        baseType: detailBaseType || undefined,
        code: detailCode.trim() || undefined,
      });
      await refreshFull();
      if (unclearable.length > 0) {
        setWarning(
          `${unclearable.join(", ")} can't be cleared once set — ${unclearable.length === 1 ? "it has" : "they have"} been restored. Pick a different value instead.`,
        );
      } else {
        setDetailSaved(true);
      }
    } catch (e) {
      // A 409 here can be a same-tenant code conflict (cloud sends a plain
      // {detail: "template code already in use"} body) rather than the usual
      // version-token race — surface that specific message when present.
      const conflictDetail =
        e instanceof HttpError && e.status === 409
          ? (e.body as { detail?: string } | undefined)?.detail
          : undefined;
      setError(conflictDetail ?? editorErrorMessage(e, "save the template details"));
      if (conflictDetail && /code.*already in use/i.test(conflictDetail)) {
        setDetailCodeError(conflictDetail);
        setSectionOpen("details");
        requestAnimationFrame(() => detailCodeRef.current?.focus());
      }
    } finally {
      setDetailSaving(false);
    }
  }, [client, full, name, detailCategory, detailModel, detailStyle, detailBaseType, detailCode, refreshFull, confirmDiscardUnsaved]);

  useEffect(() => {
    void refreshFull();
  }, [refreshFull]);

  /** Persist ONLY the template name (metadata-only — allowed on a locked/Active version).
   *  Previously the name was edited in local state and never sent, so it reverted on
   *  reload and never reached another system. */
  const [nameSaving, setNameSaving] = useState(false);
  const [nameSaved, setNameSaved] = useState(false);
  const saveName = useCallback(async () => {
    const next = name.trim();
    if (!next || next === full.name) return;
    // LB-3: this is a metadata-only PATCH, but it finishes with `refreshFull()`, which rebuilds
    // the canvas from the server payload — so fixing a typo in the name threw away unsaved masks
    // with no warning at all (Refresh, Force sync and Save details all ask). Same gate. Asked
    // BEFORE the write, but declining only skips the RE-PULL: the rename is not the destructive
    // part, so the name still saves and the operator keeps their work.
    const rePull = await confirmDiscardUnsaved("Save name");
    setNameSaving(true);
    setError(null);
    setWarning(null); // ED-17: a new action clears the previous one's notice
    try {
      await client.updateTemplateDetails(full.templateId, { name: next });
      if (rePull) await refreshFull();
      setNameSaved(true);
    } catch (e) {
      setError(editorErrorMessage(e, "save the name"));
    } finally {
      setNameSaving(false);
    }
  }, [client, full.templateId, full.name, name, refreshFull, confirmDiscardUnsaved]);

  const status = full.currentVersion.status;
  // Editing needs an OPEN, still-editable Draft. `!draft` alone was wrong after a Publish:
  // submit auto-approves (gate off), which LOCKS the version server-side, but `draft` stayed
  // truthy — so the editor looked editable while every bake 409'd ("locked") and every Save
  // 409'd into the misleading "This version changed elsewhere" message, with the recovery
  // button ("Unlock & edit") hidden because it only renders when locked. Treat a submitted
  // version that is no longer a Draft as locked, which restores that button. (audit A15)
  const draftLocked = savedStatus !== null && savedStatus !== "Draft";
  const locked = !draft || draftLocked;
  // B4: the built-in views not yet present as a draft — the "+ Add view" options.
  const addableViews = ALL_VIEWS.filter((v) => !viewDrafts.some((d) => d.view_type === v));

  /** Open a fresh editable Draft via the existing version flow (002/003).
   *
   * Failures are stored in `unlockError` (not the shared `error` banner) so the
   * "Unlock & edit" retry button can render contextually next to its own message.
   * The shared `error` banner is cleared so a previous save error does not obscure
   * the unlock failure. */
  const openDraft = useCallback(async () => {
    setBusy(true);
    setError(null);
    setUnlockError(null);
    // A fresh editable draft clears the post-publish lock derived from the LAST submit.
    setSavedStatus(null);
    setSave("idle");
    try {
      const v = await client.createVersion(full.templateId);
      latestTokenRef.current = v.versionToken;
      draftVersionRef.current = v.versionNumber;
      setDraft({ versionId: v.versionId, versionNumber: v.versionNumber, versionToken: v.versionToken });
      setUnlockError(null);
      setSave("idle");
    } catch (e) {
      setUnlockError(editorErrorMessage(e, "unlock this version"));
    } finally {
      setBusy(false);
    }
  }, [client, full.templateId]);

  // Editing should "just work" — auto-open an editable Draft when the editor opens
  // so there's no "this version is locked" friction. The backend REUSES an existing
  // Draft (so re-opening never stacks new drafts), and an Active version spawns one
  // fresh Draft. Runs once per template; failures (offline/demo) fall back to the
  // manual "Open editable draft" button.
  const autoDraftedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!draft && !busy && autoDraftedRef.current !== full.templateId) {
      autoDraftedRef.current = full.templateId;
      void openDraft();
    }
  }, [draft, busy, openDraft, full.templateId]);

  /**
   * A rollback / reopen from the version-history panel re-points the template at a DIFFERENT
   * version. The editor auto-opens a Draft the moment it mounts, so by then that Draft is
   * branched off the version being rolled away — the cloud re-seeds it from the restored version
   * and rotates its token. Re-fetching alone (the old `onChanged={refreshFull}`) left the editor
   * holding the pre-rollback draft handle: the next Save re-published the rolled-away content, or
   * 409'd on the now-stale token. `openDraft` REUSES the same draft server-side, so this just
   * re-adopts its fresh id/token on top of the refreshed payload.
   */
  const afterLifecycleChange = useCallback(async () => {
    // LB-3: `refreshFull` rebuilds the canvas from the server payload, so a rollback/reopen
    // silently discarded uncommitted masks — the fifth and last unguarded caller. The lifecycle
    // change itself has ALREADY happened server-side by the time this runs, so declining can only
    // skip the RE-PULL. `openDraft` still runs either way: it re-adopts the draft handle the
    // cloud re-seeded, without which the next Save presents a stale token and 409s.
    if (await confirmDiscardUnsaved("Restoring a version")) await refreshFull();
    await openDraft();
  }, [refreshFull, openDraft, confirmDiscardUnsaved]);

  /** ED-17 — the "Published/Saved" confirmation (and any leftover banner) must not survive the
   *  next edit. Wrapping the canvas setters is deliberate: an effect on [printArea, cutouts]
   *  would also fire on the post-Save `setNewRefUrl({})` re-render and kill the chip instantly.
   *  `setSave("idle")` when already idle is a no-op re-render (React bails on an identical value),
   *  so this is safe on every drag tick. */
  /** Run views whose mock-up was uploaded since their last key or geometry edit. */
  const bakedRunViewsRef = useRef(new Set<string>());
  const onPrintAreaEdited = useCallback((p: Polygon) => {
    bakedRunViewsRef.current.delete(activeViewRef.current);
    setPrintArea(p);
    setSave("idle");
  }, []);
  const onCutoutsEdited = useCallback((c: Mask[]) => {
    bakedRunViewsRef.current.delete(activeViewRef.current);
    setCutouts(c);
    setSave("idle");
  }, []);
  /** P2-3: dragging the amber brand box is an edit like any other geometry change. */
  const onBrandAreaEdited = useCallback((p: Polygon) => {
    setBrandArea(p);
    setSave("idle");
  }, []);
  /** P2-3: add (a centred 200×100px default) / remove the active view's brand-logo box. Removing
   *  it stashes `brand_logo_placement: null`, so the Save actually CLEARS the persisted quad. */
  const toggleBrandArea = useCallback((on: boolean) => {
    setBrandArea(on ? defaultBrandRect(baseDims) : null);
    setSave("idle");
  }, [baseDims]);

  /** WS-EXIF — the frame the ACTIVE view's stored geometry was drawn on. The editor compares it
   *  with the photo it decodes and refuses to draw a quad that belongs to a different shape. */
  const activeAuthoringFrame = useMemo(
    () => authoringFrameOf(viewDrafts.find((v) => v.view_type === activeView)),
    [viewDrafts, activeView],
  );

  /**
   * WS-EXIF — the frame to stamp on the view being stashed, or `null` to leave whatever is
   * stored alone.
   *
   * Stamping unconditionally was a data-loss bug with a very quiet face: the banner would be up
   * (the stored quad does not belong to this photo), the operator would switch to another view
   * tab, and the stash on the way out would record the CURRENT photo as the frame those numbers
   * were drawn on. Coming back, the frames now "agree", so the editor draws the wrong quad as if
   * it were right — and the next Save persists that lie to the cloud.
   *
   * So the frame is written only when the editor is not accusing this view: a legacy record with
   * no frame yet, or frames that already agree. An answered banner reaches "agree" through
   * `onAuthoringFrameResolved`, which writes the frame itself — this is not a second path.
   */
  const frameToStamp = useMemo(
    () => (frameVerdict(activeAuthoringFrame, baseDims) === "draw" ? baseDims : null),
    [activeAuthoringFrame, baseDims],
  );

  /** The operator answered the frame banner (re-fitted, or kept the quad): this photo is the
   *  view's frame from now on. Recording it on the DRAFT is what stops the banner returning on
   *  the next remount; the Save writes it to the cloud along with the geometry. */
  const onAuthoringFrameResolved = useCallback(() => {
    if (!baseDims) return;
    setViewDrafts((prev) =>
      prev.map((d) =>
        d.view_type === activeView
          ? { ...d, base_image_size: [baseDims.w, baseDims.h] as [number, number] }
          : d,
      ),
    );
    setSave("idle");
  }, [activeView, baseDims]);

  /**
   * WS-EXIF — a reference swap re-frames every polygon on the view at once. Write down the
   * OUTGOING photo's size first, so a LEGACY view (which records none) still has something for
   * the editor to compare the incoming photo against instead of silently redrawing the old quad
   * on a new frame.
   *
   * The view on the canvas is free: its photo is decoded and `baseDims` holds the answer, and
   * clearing `baseDims` afterwards stops the outgoing size being mistaken for the incoming one.
   * A SIBLING view is not — and `applyAsReference` swaps sibling views, which is precisely the
   * case with nobody watching. Its photo is measured instead, and only when that view has masks
   * to lose and no frame of its own.
   */
  const pinFrameBeforeReferenceSwap = useCallback(async (view: string) => {
    if (view === activeView) {
      setViewDrafts((prev) => pinOutgoingFrame(prev, view, baseDims));
      setBaseDims(null);
      return;
    }
    const target = viewDrafts.find((d) => d.view_type === view);
    if (!target || (target.masks?.length ?? 0) === 0 || authoringFrameOf(target)) return;
    const frame = await measureFrame(newRefUrl[view] ?? target.base_case_image_ref ?? undefined);
    setViewDrafts((prev) => pinOutgoingFrame(prev, view, frame));
  }, [activeView, baseDims, viewDrafts, newRefUrl]);

  /** Revert the ACTIVE view's working masks to the version they were loaded from. */
  const reset = useCallback(() => {
    const savedView = viewOf(full.currentVersion.payload, activeView) ?? primaryView(full.currentVersion.payload);
    const g = geometryFromView(savedView);
    setPrintArea(g.printArea);
    setCutouts(g.cutouts);
    setBrandArea(brandAreaFromView(savedView)); // P2-3: revert the brand box with the geometry
    setName(full.name);
    setSave("idle");
  }, [full.currentVersion.payload, full.name, activeView]);

  /** Capture the active view's live canvas geometry (+ brand box + any freshly-swapped reference)
   *  back into its draft. Called before every tab switch and before Save. */
  const stashActiveView = useCallback(() => {
    // WS-EXIF: `frameToStamp` is the decoded size of the reference photo, but ONLY while the
    // editor is not accusing this view of carrying geometry from a different frame — see its
    // definition. It is what lets the next reopen tell a trustworthy quad from a re-framed one,
    // and what finally gives a LEGACY view a frame.
    setViewDrafts((prev) => stashViewInto(prev, activeView, printArea, cutouts, brandArea, newRefUrl[activeView], frameToStamp));
  }, [activeView, printArea, cutouts, brandArea, newRefUrl, frameToStamp]);

  /** Switch the active view tab: stash the current view, then load the target view's geometry
   *  onto the canvas. The target view is untouched by the stash, so reading it from the current
   *  `viewDrafts` closure is correct. */
  const switchView = useCallback((next: string) => {
    if (next === activeView) return;
    // A change the bake has not taken (design fit, layers) must be baked again before Publish.
    if (bakeRef.current?.isDirty()) bakedRunViewsRef.current.delete(activeView);
    stashActiveView();
    const target = viewDrafts.find((v) => v.view_type === next);
    const g = geometryFromView(target);
    setPrintArea(g.printArea);
    setCutouts(g.cutouts);
    // The brand quad is per-view state exactly like the masks, and `stashViewInto` writes an
    // EXPLICIT null when `brandArea` is null. Leaving it on the previous tab's value therefore
    // did real damage with no brand interaction at all: switching away from a view with no box
    // ERASED the target view's saved placement on the next stash, and switching away from one
    // that HAS a box COPIED that quad onto the target view (so Export merged the seller logo
    // onto two views, at the wrong view's coordinates). It also made every tab switch look like
    // unsaved work to `hasUnsavedWork`, popping the discard prompt on Refresh/Force sync.
    setBrandArea(brandAreaFromView(target));
    setActiveView(next);
    activeViewRef.current = next; // the canvas answers for `next` before the effect would set it
    // WS-EXIF: the previous tab's decoded size must not be stamped onto this one. The loader
    // refills it when the new view's photo decodes; until then nothing stamps a frame at all.
    setBaseDims(null);
    setSave("idle");
  }, [activeView, viewDrafts, stashActiveView]);

  /** Append a new view draft (empty geometry) and activate it. No-op if already present. */
  const addView = useCallback((next: string) => {
    if (!next || viewDrafts.some((v) => v.view_type === next)) return;
    stashActiveView();
    setViewDrafts((prev) =>
      prev.some((v) => v.view_type === next)
        ? prev
        : [...prev, { view_type: next as WireView["view_type"], perspective_points: [], masks: [], layers: [] }],
    );
    setPrintArea([]);
    setCutouts([]);
    setBrandArea(null); // a brand-new view has no brand box (see `switchView` — a stale quad is copied onto it otherwise)
    setActiveView(next);
    setSave("idle");
  }, [viewDrafts, stashActiveView]);

  /** Remove a view draft. The LAST view can never be removed, and the PRIMARY (back) view can never
   *  be removed while present — it owns the legacy flat baked_assets fields (which `dropViewRefs`
   *  intentionally leaves intact) and is the de-facto primary, so dropping it would orphan those
   *  flat refs. Removing the active view falls back to the first remaining view; the removed view's
   *  baked cloud refs (if any) are pruned on the next Save via `dropViewRefs`. */
  const removeView = useCallback((target: string) => {
    if (viewDrafts.length <= 1) return;
    if (target === PRIMARY_VIEW && viewDrafts.some((v) => v.view_type === PRIMARY_VIEW)) return;
    const remaining = viewDrafts.filter((v) => v.view_type !== target);
    setViewDrafts(remaining);
    // ED-12: tombstone the removed view's baked refs NOW. Pruning only happened on Save, so
    // remove → re-add (before saving) revived the deleted view's matte/texture onto what the
    // admin sees as a brand-new blank view. `prunedEpoch` forces the editor to remount against
    // the pruned map even when the active tab doesn't change.
    prunedViewsRef.current.add(target);
    setPrunedEpoch((n) => n + 1);
    // ED-12 (residual): tombstoning only covers the CLOUD refs. The LOCAL bake for this view is
    // still in the mockup store, so `hasMockup`/`getMockup` keep answering for a view the operator
    // deleted — re-adding it would restore that matte/texture, and a render would composite over
    // the old shell. Drop it under BOTH keys it can live under: the draft's version (what the
    // editor bakes into while a rework is open) and the current version (what `hydrateBakedAssets`
    // downloaded into). Fire-and-forget: `deleteMockup` never throws and the removal must stay
    // synchronous for the UI.
    for (const vid of new Set([draft?.versionId, full.currentVersion.versionId].filter(Boolean))) {
      void deleteMockup(vid as string, target);
    }
    if (target === activeView) {
      const nextActive = remaining[0]?.view_type ?? PRIMARY_VIEW;
      const g = geometryFromView(remaining[0]);
      setActiveView(nextActive);
      setPrintArea(g.printArea);
      setCutouts(g.cutouts);
      setBrandArea(brandAreaFromView(remaining[0])); // follow the fallback view (see `switchView`)
    }
    setSave("idle");
  }, [viewDrafts, activeView, draft, full]);

  /** The Generate run views the draft holds, by view. */
  const [takenRunViews, setTakenRunViews] = useState<Record<string, TakenRunView>>({});
  const takenRunViewsRef = useRef(takenRunViews);
  takenRunViewsRef.current = takenRunViews;
  const publishedJobsRef = useRef(new Set<string>());
  /** Each run view whose print guide a submitted version holds: its job is now in this template. */
  const markRunViewsPublished = useCallback(async (submittedViews: WireView[]) => {
    for (const [view, { jobId, url }] of Object.entries(takenRunViewsRef.current)) {
      const held = submittedViews.some((d) => d.view_type === view && d.base_case_image_ref === url);
      if (!held || publishedJobsRef.current.has(jobId)) continue;
      publishedJobsRef.current.add(jobId);
      try {
        onGenerationPublished?.(await client.recordGenerationProgress(jobId, "published", full.templateId));
      } catch (e) {
        logger?.log("warn", "TemplateEditor · the generation could not be marked published", {
          error: e, jobId, templateId: full.templateId,
        });
      }
    }
  }, [client, full.templateId, logger, onGenerationPublished]);

  /** Persist the reworked masks on the open Draft (Draft → Pending). */
  /** True while the case image the camera shading fix asks for is still being made. */
  const shadingPendingRef = useRef(false);

  const saveDraft = useCallback(async () => {
    if (!draft) return;
    setBusy(true);
    setError(null);
    setWarning(null); // ED-17: a fresh attempt starts with a clean slate
    // F8 — one Save can have SEVERAL things worth saying (the case photo did not publish AND the
    // Brand colour could not be resolved), and each `setWarning` used to overwrite the last, so
    // the operator was told about whichever happened to run second. Append instead. ED-17 is
    // unaffected: the reset above still clears the previous action's notice.
    const addWarning = (m: string) => setWarning((prev) => (prev ? `${prev} ${m}` : m));
    // A "Make template" mock-up view still without a print area has no geometry to check.
    const markedEmpty = printArea.length === 0
      && !!viewDrafts.find((d) => d.view_type === activeView)?.mockup_only;
    try {
      const v = markedEmpty ? null : await engine.geometryValidate(printArea, cutouts);
      if (v && !v.valid) {
        // F-5 (c) — the Publish button STAYS enabled and this is not a generic failure. The
        // plain reason goes in the error banner, and the geometry banner above it is already
        // on screen carrying the one-click "Fix cutouts". `Invalid: cutout_not_subtracted`
        // was an engine code in a folded section; this is a sentence with an action beside it.
        setError(`${geometry.geometryProblemMessage(v.errors)} Nothing was published. Use “Fix cutouts” above, or edit the shapes on the canvas.`);
        return;
      }
      const masks = toWireMasks(printArea, cutouts);
      // B4: stash the active view's live geometry (+ swapped reference) into its draft, then submit
      // the FULL `viewDrafts` set. `nextDrafts` is captured synchronously (setViewDrafts is async)
      // so the submit below sends the just-stashed views, not the stale closure value.
      let nextDrafts = stashViewInto(viewDrafts, activeView, printArea, cutouts, brandArea, newRefUrl[activeView], frameToStamp);
      setViewDrafts(nextDrafts);
      // LB-1: `geometryValidate` above covers the ACTIVE view only; the cloud validates EVERY view
      // and rolls the whole submit back on the first bad one — so adding a tab to mask later made
      // every Publish fail, discarding the sibling views that were correct, with a message naming
      // no view. Refuse locally instead, naming the tab to open. Runs AFTER the stash so the
      // just-drawn active view counts.
      const viewProblems = validateViewsForPublish(nextDrafts);
      if (viewProblems.length > 0) {
        setError(viewProblemsMessage(viewProblems));
        return; // finally clears busy
      }
      // BACKFILL a device-local-only case photo. Wizard-created templates stored the case image
      // ONLY in this device's local store and persisted NO `base_case_image_ref`, so every other
      // system opened this template to a BLANK editor canvas. When the primary view still has no
      // hosted ref (and none is staged), publish the local copy now and persist it with this Save.
      // Best-effort: any failure warns + proceeds — the backfill must never block the Save itself.
      const primaryDraft = nextDrafts.find((v) => v.view_type === PRIMARY_VIEW);
      if (primaryDraft && !primaryDraft.base_case_image_ref && !newRefUrl[PRIMARY_VIEW]) {
        try {
          const bytes = await loadTemplateImageBytes(engine, full.templateId);
          if (bytes && bytes.length > 4) {
            // Magic-sniff the real format so the presigned ext/Content-Type match the bytes.
            const ext = bytes[0] === 0xff && bytes[1] === 0xd8 ? "jpg"
              : bytes[0] === 0x52 && bytes[1] === 0x49 ? "webp"
              : "png";
            const target = await client.templateReferenceUpload(full.templateId, ext);
            if (target.upload_url.startsWith("file://") || !isDurableRef(target.public_url)) {
              // Local hosting / a root-relative public URL can't serve other devices — skip,
              // and SAY SO where the operator is looking (a warn log alone hid this).
              logger?.log("warn", "Save: hosting can't publish the case photo for other devices (local-only target or non-durable public URL)", {
                step: "caseImagePublish", templateId: full.templateId,
              });
              addWarning("Saved — but the case photo could not be published (hosting is local-only or misconfigured). Other systems' editors will show a blank canvas for this template until a Save succeeds with working hosting.");
            } else {
              const mime = ext === "jpg" ? "image/jpeg" : ext === "webp" ? "image/webp" : "image/png";
              const res = await fetchWithTimeout(target.upload_url, {
                method: "PUT", headers: { "Content-Type": mime }, body: bytes as BodyInit,
              }, UPLOAD_TIMEOUT_MS, "Case photo publish");
              if (!res.ok) throw new Error(`case-photo publish PUT -> ${res.status}`);
              nextDrafts = nextDrafts.map((d) =>
                d.view_type === PRIMARY_VIEW ? { ...d, base_case_image_ref: target.public_url } : d,
              );
              setViewDrafts(nextDrafts);
              logger?.breadcrumb("action", "Save: published the device-local case photo (cross-device editor fix)", {
                templateId: full.templateId, bytes: bytes.length, ext,
              });
            }
          }
        } catch (e) {
          logger?.log("warn", "Save: couldn't publish the device-local case photo — other systems' editors stay blank until a Save succeeds here", {
            error: e, step: "caseImagePublish", templateId: full.templateId,
          });
          // Surface it (non-blocking — the Save itself continues below). Silent-warn was the
          // exact failure mode that kept the blank-canvas bug invisible for weeks.
          addWarning(`Saved — but the case photo publish failed (${e instanceof Error ? e.message : String(e)}). Other systems' editors will show a blank canvas until a Save succeeds here.`);
        }
      }
      // Task 6: persist the per-template background into the draft render_settings BEFORE the
      // bake/submit. patch_template REPLACES render_settings wholesale, so send the FULL current
      // settings merged with the chosen background — never just the delta, or formats/output_size/
      // etc. would reset to schema defaults. It rotates the version token, so re-read it
      // (createVersion reuses the open draft with the fresh token) and adopt it into every ref
      // before the bake/submit below.
      //
      // Fire the patch when the operator SET a background OR is CLEARING one that was persisted.
      // Picking "— None —" ("") after a real background was saved must write `background: null`
      // (cloud RenderSettings.background: str | None) — the old `if (background)` guard skipped
      // the patch, so the cloud silently kept the stale value while the UI showed None + Saved.
      // Compat unchanged: absent persisted + empty selection ⇒ NO patch (absent stays absent).
      //
      // P0-1: the CLEAR half additionally requires the operator to have TOUCHED the picker. The
      // picker seeds from the list row (no payload) and only catches up once `refreshFull` lands,
      // so an untouched "" is "we don't know yet", NOT "the operator chose None" — writing null on
      // it deleted the background on every Publish. A real "— None —" pick sets the touched flag.
      const rs = full.currentVersion.payload?.render_settings;
      const persistedBackground = rs?.background ?? "";
      const clearingBackground = !background && !!persistedBackground && backgroundTouchedRef.current;
      if (background || clearingBackground) {
        // Non-empty selection persists that choice; a deliberate empty selection CLEARS to null.
        const nextBackground = background ? background : null;
        // WS-BG: resolve the COLOUR the choice names, in the same patch. `brand_color` is
        // re-resolved from the live brand list on EVERY Save — never copied from the stored
        // value — so recolouring the brand propagates to this template on its next Save (and
        // only then: a locked version renders from its own payload on other devices, with no
        // brand list to consult). A brand list that fails to load leaves the colour NULL rather
        // than inventing one, and the operator is told.
        let brandColor: string | null = null;
        if (nextBackground === "brand_color") {
          try {
            brandColor = brandColorPrimary(await client.listBrands());
          } catch (e) {
            logger?.log("warn", "Save: couldn't read the brand list — the Brand color background stays unresolved", {
              error: e, step: "backgroundBrandColor", templateId: full.templateId,
            });
          }
        }
        const nextBackgroundColor = resolveBackgroundColor(nextBackground, {
          solidHex: backgroundHex, brandColor,
        });
        logger?.breadcrumb("action", "Save · patching render_settings.background", {
          templateId: full.templateId, versionNumber: draft.versionNumber, background: nextBackground,
          backgroundColor: nextBackgroundColor,
        });
        await client.patchRenderSettings(full.templateId, {
          formats: rs?.formats,
          output_size: rs?.output_size,
          fit_policy: rs?.fit_policy,
          shadow_preset: rs?.shadow_preset,
          reflection_preset: rs?.reflection_preset,
          print_dpi: rs?.print_dpi,
          background: nextBackground,
          background_color: nextBackgroundColor,
        });
        // F8: AFTER the patch, not before. "Saved — but…" was previously said while the save
        // could still throw, which would have left the operator reading that a save succeeded
        // when it had not.
        if (nextBackground === "brand_color" && !brandColor) {
          addWarning("Saved — but your default brand has no primary colour, so the Brand color background renders white. Set one in Branding (or make the brand you want the default), then Save again.");
        }
        const refreshed = await client.createVersion(full.templateId);
        latestTokenRef.current = refreshed.versionToken;
        draftVersionRef.current = refreshed.versionNumber;
        setDraft({
          versionId: refreshed.versionId,
          versionNumber: refreshed.versionNumber,
          versionToken: refreshed.versionToken,
        });
      }
      // A3: if the mask editor has bake-relevant appearance changes not yet uploaded (AI edge,
      // rotation, fill, aspect, texture / clear-bg, layer opacity…), force a bake+upload FIRST so
      // the cloud's baked assets match the visible state — otherwise Save persists the geometry
      // while other devices keep rendering the STALE (or absent) baked layers. The bake's own
      // upload failure is NON-BLOCKING (the retry banner surfaces it); we only ensure the attempt
      // happens on a healthy network. A clean editor (e.g. the admin just did a removal, which
      // Task 1 already baked) short-circuits — no redundant re-bake.
      // A changed print guide finish (flatten, camera shading fix) changes the keyed image without
      // touching the bake's dirty flag, so the cloud would keep the old layers: bake from the new.
      const shadingChanged = finishKey(viewDrafts.find((d) => d.view_type === activeView))
        !== finishKey(viewOf(full.currentVersion.payload, activeView));
      if (shadingChanged) {
        await waitFor(() => !shadingPendingRef.current && Boolean(bakeRef.current?.sourceReady()), SHADING_LOAD_MS);
      }
      if (bakeRef.current && (shadingChanged || bakeRef.current.isDirty())) {
        logger?.breadcrumb("action", "Save · baking pending mask changes before submit", {
          templateId: full.templateId, versionNumber: draft.versionNumber,
        });
        // bakeNow() emits `onBaked` synchronously (→ syncBakedAssets, which REPLACES
        // pendingSyncRef with this fresh bake's upload), so the wait below naturally covers it.
        const baked = await bakeRef.current.bakeNow();
        // No-emit: the bake claimed to be dirty but produced no upload (degenerate legacy template,
        // decode failure, missing canvas — previewMockup's silent early-returns). Save used to
        // report success while the cloud assets stayed stale, with only a detached warn as a trace.
        // Surface it: warn WITH detail + flip the retry banner so the admin knows other devices may
        // render design-only. (Save still proceeds — a stuck bake must not block the geometry save.)
        if (!baked && (shadingChanged || bakeRef.current.isDirty())) {
          logger?.log("warn", "Save: forced bake produced no upload — cross-device assets may be stale", {
            templateId: full.templateId, versionNumber: draft.versionNumber,
          });
          setBakedSync("failed");
          setBakedSyncDismissed(false);
        }
      }
      // #1: a template with NO baked mockup shell exports design-only (the design warped over a flat
      // background, no phone/case). Auto-bake happens above when a shell is available; if the version
      // STILL has no bake — locally (this session) or in the cloud refs — try once more, then BLOCK
      // the Save so a design-only template can't be published silently. A real product template bakes
      // as soon as the admin removes the product photo's background, so this only trips on a template
      // that never had a usable product photo / marker.
      const cloudHasBake = hasAnyBakedView(lastKnownRefsRef.current);
      // The local signal must cover EVERY view of the template, not just the active one: a sibling
      // view baked in-session lives in mem under its own key, and the cloud snapshot lags an
      // in-flight (or failed) sync — an active-view-only check would falsely block a multi-view
      // template that already has a real baked view.
      const bakeVersionId = draft?.versionId ?? full.currentVersion.versionId;
      const localViews = viewDrafts.map((v) => v.view_type);
      const anyLocalBake = () =>
        (bakeRef.current?.hasBake() ?? false) || localViews.some((v) => hasMockup(bakeVersionId, v));
      let localHasBake = anyLocalBake();
      // A template whose every view is still mock-up only has nothing to bake or place a design in.
      const mockupOnly = nextDrafts.every((d) => d.mockup_only);
      if (!cloudHasBake && !localHasBake && bakeRef.current && !mockupOnly) {
        // Last-chance bake: covers a shell that's available but was never previewed/removed.
        const baked = await bakeRef.current.bakeNow();
        localHasBake = baked || anyLocalBake();
      }
      // Only enforce when the mask editor is mounted (bakeRef live) — a save path without it can't
      // assess bake state, so it must never be blocked.
      if (bakeRef.current && !cloudHasBake && !localHasBake && !mockupOnly) {
        // ED-4: when the case image never DECODED (404 / CORS / dead host) a bake is physically
        // impossible — "Remove background (AI)" and "Preview mockup" are both disabled in exactly
        // that state. The old copy sent the operator to press them and hard-blocked the Save, so
        // their geometry edits were unsaveable with no way forward. Name the real cause, point at
        // Force sync, and let the geometry save (the bake can be added once the image resolves).
        if (!bakeRef.current.sourceReady()) {
          const why = bakeRef.current.caseImageFailed()
            ? "the case image couldn't be loaded from hosting (404 / CORS / unreachable). Press “Force sync” above to re-pull it — if that doesn't help, check the hosting CORS policy."
            : "the case image hasn't loaded yet. Wait for the preview to appear (or press “Force sync”), then Save again to build the mock-up.";
          logger?.log("warn", "Save: no baked mockup and no usable case image — saving geometry only", {
            templateId: full.templateId, versionNumber: draft.versionNumber,
            caseImageFailed: bakeRef.current.caseImageFailed(),
          });
          addWarning(
            `Saved the geometry, but this template still has no mock-up — ${why} Until then it would export the design only (no phone/case).`,
          );
        } else {
          logger?.log("warn", "Save blocked: template has no baked mockup shell (would export design-only)", {
            templateId: full.templateId, versionNumber: draft.versionNumber,
          });
          setError(
            "This template has no mock-up yet, so it would export the design only — no phone/case. " +
              "Upload the product photo and click “Remove background (AI)” (or “Preview mock-up”) to build the mock-up, then Save.",
          );
          return; // finally clears busy
        }
      }
      // Serialize Save behind any in-flight baked-asset sync (the bake just forced above, or a
      // still-uploading Preview). submitVersion must NOT fire while `setBakedAssets` is mid-flight
      // rotating the version_token — that races the token adoption and 409s the user's OWN
      // single-device edit ("this version changed elsewhere"). A FAILED sync must never block Save
      // (the banner covers it); a WEDGED sync must never hang Save (SAVE_SYNC_TIMEOUT_MS fallback).
      const pending = pendingSyncRef.current;
      if (pending) {
        logger?.breadcrumb("action", "Save · waiting for asset sync", {
          templateId: full.templateId, versionNumber: draft.versionNumber,
        });
        let timer: ReturnType<typeof setTimeout> | undefined;
        const timedOut = await Promise.race([
          pending.then(() => false, () => false),
          new Promise<boolean>((resolve) => { timer = setTimeout(() => resolve(true), SAVE_SYNC_TIMEOUT_MS); }),
        ]);
        if (timer) clearTimeout(timer);
        if (timedOut) {
          logger?.log("warn", "Save: asset sync did not settle within timeout — submitting with the current token", {
            templateId: full.templateId, versionNumber: draft.versionNumber, timeoutMs: SAVE_SYNC_TIMEOUT_MS,
          });
        }
      }
      // The sync's token adoption ran DURING the await (async setDraft), so `draft.versionToken`
      // in this closure may be stale. Read the freshest token from the ref (updated at every
      // adoption/rotation) so submitVersion presents the post-rotation token, not the 409-bait one.
      const versionToken = latestTokenRef.current ?? draft.versionToken;
      // B4: submit the FULL per-view draft set — the server takes `views` wholesale, so every view
      // (touched or not) round-trips field-for-field, and any swapped reference / new view persists.
      // `masks` is the ACTIVE view's masks (the submit contract still requires the primary masks
      // arg); `views` carries all of them. The old single-view collapse + newRefUrl-gated branch are
      // gone — this is always the 6-arg call now.
      logger?.breadcrumb("action", "Save: submitting version to cloud", {
        templateId: full.templateId, versionNumber: draft.versionNumber, viewCount: nextDrafts.length,
      });
      // P2-3: the brand-logo placement rides on each view's `brand_logo_placement` (stashed above).
      // The separate 5th argument is the LEGACY single-view channel; send the PRIMARY view's quad
      // there too so a server that still reads that argument gets the same answer as one reading
      // the `views` branch. Was hard-coded `null`, which was the "no authoring path" symptom.
      const primaryBrand = (nextDrafts.find((v) => v.view_type === PRIMARY_VIEW)
        ?.brand_logo_placement ?? null) as [number, number][] | null;
      const submitted = await client.submitVersion(
        full.templateId,
        draft.versionNumber,
        versionToken,
        masks,
        primaryBrand,
        nextDrafts as unknown as { view_type: string; masks: unknown; layers?: unknown[] }[],
      );
      latestTokenRef.current = submitted.versionToken;
      draftVersionRef.current = submitted.versionNumber;
      setDraft({ versionId: submitted.versionId, versionNumber: submitted.versionNumber, versionToken: submitted.versionToken });
      setNewRefUrl({});
      // The staged references were just persisted — clear the "Applied as <View> reference"
      // line so it doesn't linger past the Save that consumed it.
      setGenAppliedView(null);
      // Drive the "saved" line off the REAL returned status, not the local approvals guess.
      setSavedStatus(submitted.status);
      setSave("saved");
      setPanelRefresh((n) => n + 1);
      void markRunViewsPublished(nextDrafts);
      logger?.breadcrumb("action", "Save: version submitted to cloud", {
        templateId: full.templateId, versionNumber: submitted.versionNumber, versionId: submitted.versionId,
      });
      // B4: if the admin REMOVED a view that had baked cloud refs, prune those now-orphaned view
      // entries (mergeViewRefs preserves siblings, so a removal is only reflected here). Best-effort
      // + non-blocking: a failure never fails the Save. Adopt the rotated token on success.
      // Base the prune on the LIVE refs snapshot (threaded forward across same-session bakes), not
      // the stale mount snapshot — otherwise this rebuilds from the open-time value and would wipe
      // kept views' same-session bakes.
      const lastKnownRefs = lastKnownRefsRef.current;
      const keptViews = new Set<string>(nextDrafts.map((v) => v.view_type));
      // ED-12: a view the admin REMOVED must be pruned even if they RE-ADDED it before saving —
      // the re-added tab is a blank view, so its old matte/texture refs are orphans either way.
      const tombstoned = prunedViewsRef.current;
      const orphanViews = Object.keys(lastKnownRefs?.views ?? {})
        .filter((v) => !keptViews.has(v) || tombstoned.has(v));
      if (orphanViews.length > 0) {
        const cleaned = dropViewRefs(lastKnownRefs, orphanViews);
        if (cleaned && cleaned !== lastKnownRefs) {
          try {
            logger?.breadcrumb("action", "Save: pruning baked refs for removed views", {
              templateId: full.templateId, versionNumber: submitted.versionNumber, orphanViews,
            });
            const pruned = await client.setBakedAssets(
              full.templateId, submitted.versionNumber, cleaned as unknown as Record<string, unknown>,
            );
            // Advance the live snapshot to the pruned map (fresh server truth for this version).
            lastKnownRefsRef.current = cleaned ?? null;
            prunedViewsRef.current = new Set(); // the cloud now agrees — tombstones discharged
            latestTokenRef.current = pruned.versionToken;
            draftVersionRef.current = pruned.versionNumber;
            setDraft((d) => (d && d.versionNumber === pruned.versionNumber ? { ...d, versionToken: pruned.versionToken } : d));
          } catch (e) {
            logger?.log("warn", "Save: failed to prune baked refs for removed views (non-blocking)", {
              error: e, step: "dropViewRefs", templateId: full.templateId, orphanViews,
            });
          }
        }
      }
    } catch (e) {
      logger?.log("error", "Save FAILED (submitVersion)", {
        error: e, step: "submitVersion",
        templateId: full.templateId, versionNumber: draft.versionNumber,
      });
      setError(editorErrorMessage(e, "publish this version"));
    } finally {
      setBusy(false);
    }
  }, [client, engine, draft, full.templateId, full.currentVersion.payload, full.currentVersion.bakedAssets, printArea, cutouts, brandArea, newRefUrl, viewDrafts, activeView, background, backgroundHex, frameToStamp, logger, markRunViewsPublished]);

  /** Item 5A: persist ownership/visibility metadata. Sends only the fields the
   *  admin actually picked (others stay ""→omitted), then hydrates the selects from
   *  the canonical PATCH response so the UI reflects the saved state (no faked read). */
  const saveOwnership = useCallback(async () => {
    setOwnerSaving(true);
    setError(null);
    setWarning(null); // ED-17: a new action clears the previous one's notice
    try {
      const r: TemplateOwnership = await client.setTemplateOwnership(full.templateId, {
        ...(visibility ? { visibility } : {}),
        ...(ownershipType ? { ownership_type: ownershipType } : {}),
        ...(syncPermission ? { sync_permission: syncPermission } : {}),
        ...(ownerViewType ? { view_type: ownerViewType } : {}),
      });
      if (r.visibility) setVisibility(r.visibility);
      if (r.ownership_type) setOwnershipType(r.ownership_type);
      if (r.sync_permission) setSyncPermission(r.sync_permission);
      if (r.view_type) setOwnerViewType(r.view_type);
      setOwnerSaved(true);
    } catch (e) {
      setError(editorErrorMessage(e, "save the ownership settings"));
    } finally {
      setOwnerSaving(false);
    }
  }, [client, full.templateId, visibility, ownershipType, syncPermission, ownerViewType]);

  /** Item 5D: presign → PUT the bytes to R2/CDN → hold the public url for the next
   *  submit. Mirrors the options/wizard reference-upload pattern; pixels never hit
   *  the API. The new image shows immediately in the MaskEditor via `caseImageUrl`. */
  const replaceReference = useCallback(
    async (file: File) => {
      setRefUploading(true);
      setError(null);
      try {
        const ext = (file.name.split(".").pop() || "png").toLowerCase();
        const target = await client.templateReferenceUpload(full.templateId, ext);
        if (target.upload_url.startsWith("file://") || !isDurableRef(target.public_url)) {
          throw new Error("Hosting is local-only or misconfigured — the reference can't be published for other devices. Check Settings → Hosting.");
        }
        const res = await fetchWithTimeout(target.upload_url, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        }, UPLOAD_TIMEOUT_MS, "Reference upload");
        if (!res.ok) throw new Error(`Upload failed (${res.status}).`);
        // B4: the replaced reference applies to the ACTIVE view only.
        await pinFrameBeforeReferenceSwap(activeView);
        setNewRefUrl((prev) => ({ ...prev, [activeView]: target.public_url }));
      } catch (e) {
        setError(editorErrorMessage(e, "replace the reference image"));
      } finally {
        setRefUploading(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    },
    [client, full.templateId, activeView, pinFrameBeforeReferenceSwap],
  );

  // --- Task B5: generate per-view references ---------------------------------
  // Poll the enqueued generation jobs to terminal (reuses the customer flow's poll hook +
  // status helpers — no reinvented queue affordance). `liveGenJobs` merges the freshest
  // snapshot over each enqueued entry.
  const genJobIds = useMemo(() => genJobs.map((j) => j.id), [genJobs]);
  // LB-4(b): `error` + `retry` are the hook's give-up channel — after MAX_DEAD_POLL_TICKS dead
  // ticks it STOPS the interval and sets a message. Reading only `snapshots` left the rows pulsing
  // on "Queued" forever with no error and no way back short of reloading the app.
  const { snapshots: genSnapshots, error: genPollError, retry: retryGenPoll } =
    useGenerationJobs(client, genJobIds, {
      onGone: (id) => setGenJobs((prev) => prev.filter((j) => j.id !== id)),
    });
  const liveGenJobs = useMemo(
    () => genJobs.map((j) => ({ ...j, job: genSnapshots[j.id] ?? j.job })),
    [genJobs, genSnapshots],
  );
  const genReviewEntry = liveGenJobs.find((e) => e.id === genReviewId);

  /** Upload the single reference-mock image used as the generation input (mirrors GeneratePanel). */
  const pickGenDesign = useCallback(async () => {
    setError(null);
    try {
      const files = await io.pickFiles({
        accept: ["image/png", "image/jpeg", "image/webp"],
        multiple: false,
        maxBytes: 32 * 1024 * 1024,
      });
      if (files.length === 0) return;
      const f = files[0];
      setGenDesign({ name: f.name, bytes: f.bytes, mime: f.mime });
    } catch (e) {
      setError(editorErrorMessage(e, "pick the design image"));
    }
  }, [io]);

  /**
   * ENQUEUE one server-side generation per selected view (N views → N `enqueueGeneration`
   * calls, each for ONE view), scoped to this template. Returns immediately — the poll hook
   * drives per-view status. Mirrors GeneratePanel's enqueue loop.
   */
  const generateViews = useCallback(async () => {
    if (!genDesign || !viewsAxisGroup || genSelectedViews.length === 0) return;
    setError(null);
    setGenJobs([]);
    // Clear the stale "Applied as <View> reference" line from a PRIOR batch — a fresh
    // generation hasn't been applied yet, so the line must not linger across batches.
    setGenAppliedView(null);
    // LB-4(a): commit each enqueued job to STATE as it lands. The loop used to accumulate into a
    // local array and only call `setGenJobs` after it completed, so a throw part-way through
    // dropped every job the server had ALREADY accepted (and is billing for) — and `setGenJobs([])`
    // above had already cleared the previous batch, so they were unreachable from the UI forever.
    let queued = 0;
    const product = phoneModels.find((p) => p.id === full.phoneModelId);
    // No product type: the cloud takes the template's own, which its catalog check need not know.
    const details = product ? { brand: product.brand, model: product.model } : undefined;
    try {
      for (const view of genSelectedViews) {
        const choice = viewsAxisGroup.choices.find((c) => c.value === view);
        if (!choice) continue;
        const enqueued = await client.enqueueGeneration(genDesign.bytes, [choice.value], {
          optionChoiceIds: [choice.id],
          templateId: full.templateId,
          mime: genDesign.mime,
          details,
          clearReview: true,
        });
        setGenJobs((prev) => [...prev, ...enqueued.map((job) => ({ id: job.id, view: choice.value, job }))]);
        queued += 1;
      }
    } catch (e) {
      // Say how far it got: the surviving rows on screen are real, in-flight work.
      const reason = isAdminViewer ? editorErrorMessage(e, "queue the view generation")
        : customerErrorText(errorDetail(e));
      setError(`Queued ${queued} of ${genSelectedViews.length} — ${reason}`);
    }
  }, [genDesign, viewsAxisGroup, genSelectedViews, client, full.templateId, full.phoneModelId,
    phoneModels, isAdminViewer]);

  /**
   * Apply a finished generation as view V's staged reference. The generation `resultUrl` is a
   * SIGNED (expiring) R2 link, so it must NOT be persisted directly as `base_case_image_ref` —
   * fetch its bytes and re-host them via the durable reference-upload presign (exactly like
   * `replaceReference`), then stage the durable public url into `newRefUrl[V]` (applied on the
   * next Save).
   *
   * The editor picker lists ALL axis views, but `viewDrafts` holds only AUTHORED views. If V has
   * an existing draft we write the ref into it; if it does NOT, we CREATE the draft here (mirror
   * of `addView`'s construction) — otherwise the re-hosted reference would silently vanish on Save
   * (a bare `newRefUrl[V]` is only consumed for the ACTIVE view, and a `.map` over a missing draft
   * is a no-op). This matches the product intent "add views by generating them".
   */
  /** A generated image's bytes, re-hosted as a durable template reference (signed links expire). */
  const hostBytes = useCallback(
    async (bytes: Uint8Array, mediaType: string): Promise<string> => {
      const ext = mediaType.includes("jpeg") ? "jpg" : mediaType.includes("webp") ? "webp" : "png";
      const target = await client.templateReferenceUpload(full.templateId, ext);
      if (target.upload_url.startsWith("file://") || !isDurableRef(target.public_url)) {
        throw new Error("Hosting is local-only or misconfigured — the generated reference can't be published for other devices. Check Settings → Hosting.");
      }
      const res = await fetchWithTimeout(target.upload_url, {
        method: "PUT",
        headers: { "Content-Type": mediaType || "application/octet-stream" },
        body: bytes as BodyInit,
      }, UPLOAD_TIMEOUT_MS, "Reference upload");
      if (!res.ok) throw new Error(`Upload failed (${res.status}).`);
      return target.public_url;
    },
    [client, full.templateId],
  );
  const hostGenerated = useCallback(
    async (url: string): Promise<string> => {
      const { bytes, mediaType } = await fetchResultBytes(url);
      return hostBytes(bytes, mediaType);
    },
    [hostBytes],
  );

  /**
   * The Draft's copy of a replaced view's baked mock-up belongs to the old image
   * (`new_draft_version` copies the active version's assets). Drop it from the Draft until the
   * view is baked again; a replaced primary view drops the flat legacy fields too.
   */
  const dropReplacedRefs = useCallback(async (views: string[]) => {
    const refs = lastKnownRefsRef.current;
    let cleaned = dropViewRefs(refs, views) ?? null;
    // The cloud's flat fields are optional; with none, the primary view has no mock-up.
    if (cleaned && views.includes(PRIMARY_VIEW)) cleaned = { views: cleaned.views } as BakedAssetRefsMap;
    if (!cleaned || cleaned === refs) return;
    lastKnownRefsRef.current = cleaned;
    setPrunedEpoch((n) => n + 1);
    const versionNumber = draftVersionRef.current;
    if (versionNumber === null) return;
    try {
      const updated = await client.setBakedAssets(full.templateId, versionNumber, cleaned as unknown as Record<string, unknown>);
      latestTokenRef.current = updated.versionToken;
      setDraft((d) => (d && d.versionNumber === updated.versionNumber ? { ...d, versionToken: updated.versionToken } : d));
    } catch (e) {
      logger?.log("warn", "TemplateEditor · a replaced view's old mock-up could not be dropped from the Draft", {
        error: e, templateId: full.templateId, views,
      });
    }
  }, [client, full.templateId, logger]);

  /**
   * Owner, 2026-10-23: a Generate run's late or regenerated views join THIS template. Each print
   * guide is hosted like "Use as reference", then added as a view or replaces that view's case
   * image in the Draft; a replaced view loses its geometry, brand box and old mock-up, so the
   * automatic key finds the new print area (a key keeps a print area the view arrives with).
   * Waits for the full record, which rebuilds the drafts, and for the Draft itself.
   */
  const runViewsTakenRef = useRef(false);
  const viewDraftsRef = useRef(viewDrafts);
  viewDraftsRef.current = viewDrafts;
  const draftOpen = Boolean(draft);
  useEffect(() => {
    if (!runViews?.length || !recordLoaded || !draftOpen || runViewsTakenRef.current) return;
    runViewsTakenRef.current = true;
    void (async () => {
      try {
        const hosted = await Promise.all(runViews.map(async (rv) => ({
          rv, url: await hostBytes(rv.image.bytes, rv.image.mediaType),
        })));
        const urlOf = new Map(hosted.map(({ rv, url }) => [rv.view, url]));
        const prev = viewDraftsRef.current;
        const replaced = prev.filter((d) => urlOf.has(d.view_type)).map((d) => d.view_type);
        const next: WireView[] = [
          ...prev.map((d) => {
            const url = urlOf.get(d.view_type);
            if (!url) return d;
            const { base_image_size: _oldFrame, ...rest } = d;
            return { ...rest, base_case_image_ref: url, perspective_points: [], masks: [], brand_logo_placement: null };
          }),
          ...hosted.filter(({ rv }) => !prev.some((d) => d.view_type === rv.view)).map(({ rv, url }) => ({
            view_type: rv.view as WireView["view_type"],
            base_case_image_ref: url,
            perspective_points: [],
            masks: [],
            layers: [],
          })),
        ];
        await dropReplacedRefs(replaced);
        setNewRefUrl((refs) => ({ ...refs, ...Object.fromEntries(urlOf) }));
        setViewDrafts(next);
        setTakenRunViews(Object.fromEntries(hosted.map(({ rv, url }) => [rv.view, {
          jobId: rv.jobId, url, clear: rv.clear, productHeightPct: rv.image.productHeightPct,
        }])));
        setActiveView(runViews[0].view);
        setPrintArea([]);
        setCutouts([]);
        setBrandArea(null);
        setBaseDims(null);
        setSave("idle");
      } catch (e) {
        setError(editorErrorMessage(e, "add the generated views"));
      }
    })();
  }, [runViews, recordLoaded, draftOpen, hostBytes, dropReplacedRefs]);
  const activeRunView = takenRunViews[activeView];

  /** Each run view's automatic key verdict, and the wait on the next one. */
  const runKeysRef = useRef<Record<string, AutoKeyVerdict>>({});
  const keyWaiterRef = useRef<(() => void) | null>(null);
  const onRunViewKey = (view: string, verdict: AutoKeyVerdict) => {
    runKeysRef.current = { ...runKeysRef.current, [view]: verdict };
    bakedRunViewsRef.current.delete(view);
    keyWaiterRef.current?.();
  };
  const keyAnswered = (view: string) => new Promise<void>((resolve) => {
    keyWaiterRef.current = () => { if (runKeysRef.current[view]) resolve(); };
    keyWaiterRef.current();
  });
  const printAreaRef = useRef(printArea);
  printAreaRef.current = printArea;
  const switchViewRef = useRef(switchView);
  switchViewRef.current = switchView;
  const saveDraftRef = useRef(saveDraft);
  saveDraftRef.current = saveDraft;
  const hasPrintArea = (view: string) => (view === activeViewRef.current
    ? printAreaRef.current
    : geometryFromView(viewDraftsRef.current.find((d) => d.view_type === view)).printArea).length > 0;
  /** Lets React commit what the last await set, so the refs above read it. */
  const settle = () => new Promise((r) => setTimeout(r, 0));

  /**
   * Publish keys and bakes every run view first, as the wizard does. A run view without a print
   * area, or not baked since its key (an automatic key uploads no bake, and leaving a view does not
   * bake it), is shown on the one canvas, keyed and baked, one at a time, each under its own
   * deadline. Coming back on screen, a view is keyed again, so its key is waited for afresh. A view
   * whose key or bake fails stops the publish with that view on screen.
   */
  const keyRunViews = async (): Promise<boolean> => {
    for (const view of Object.keys(takenRunViewsRef.current)) {
      if (hasPrintArea(view) && bakedRunViewsRef.current.has(view)) continue;
      if (view !== activeViewRef.current) {
        const { [view]: _old, ...others } = runKeysRef.current;
        runKeysRef.current = others;
        switchViewRef.current(view);
        await settle();
      }
      if (!runKeysRef.current[view]) {
        try {
          await withTimeout(keyAnswered(view), AUTOKEY_TIMEOUT_MS, "Print area key");
        } catch {
          runKeysRef.current = { ...runKeysRef.current, [view]: "not-found" };
        } finally {
          keyWaiterRef.current = null;
        }
        await settle();
      }
      const label = VIEW_LABEL[view] ?? view;
      if (!hasPrintArea(view)) {
        // A customer's placement rail moves corners but cannot draw a print area.
        setError(placementOnly
          ? `The ${label} view's print area could not be found. Regenerate that view, or ask your workspace admin to place it.`
          : `The ${label} view's print area could not be found. Draw it, then publish again.`);
        return false;
      }
      const bake = bakeRef.current;
      const baked = bake
        ? await withTimeout(bake.bakeNow(), AUTOKEY_TIMEOUT_MS, "Print area bake").catch(() => false)
        : false;
      if (!baked) {
        setError(`The ${label} view has no mock-up yet. Publish again, or check its print area.`);
        return false;
      }
      bakedRunViewsRef.current.add(view);
    }
    return true;
  };
  const publish = async () => {
    if (Object.keys(takenRunViewsRef.current).length === 0) return saveDraft();
    setError(null);
    setBusy(true);
    let ready = false;
    try {
      ready = await keyRunViews();
      await settle();
    } catch (e) {
      setError(editorErrorMessage(e, "publish this version"));
    } finally {
      if (!ready) setBusy(false);
    }
    if (ready) await saveDraftRef.current();
  };

  const applyAsReference = useCallback(
    async (view: string, resultUrl: string) => {
      setError(null);
      try {
        const publicUrl = await hostGenerated(resultUrl);
        await pinFrameBeforeReferenceSwap(view);
        setNewRefUrl((prev) => ({ ...prev, [view]: publicUrl }));
        setViewDrafts((prev) =>
          prev.some((d) => d.view_type === view)
            ? prev.map((d) => (d.view_type === view ? { ...d, base_case_image_ref: publicUrl } : d))
            : [
                ...prev,
                {
                  view_type: view as WireView["view_type"],
                  base_case_image_ref: publicUrl,
                  perspective_points: [],
                  masks: [],
                  layers: [],
                },
              ],
        );
        setGenAppliedView(view);
      } catch (e) {
        setError(editorErrorMessage(e, "apply the generated reference"));
      }
    },
    [hostGenerated, pinFrameBeforeReferenceSwap],
  );

  /**
   * The ONLY path into the batch. Resolve the (possibly re-edited) template into
   * a selection carrying its render geometry and queue it; routing to Batch is
   * left to the caller's nav so opening/editing never auto-routes.
   */
  const sendToBatch = useCallback(async () => {
    const customerId = useSessionStore.getState().session?.customerId ?? "self";
    // Re-fetch the latest full template so a just-saved re-mask is reflected, and so a
    // load failure surfaces its real cause instead of a misleading "no saved geometry"
    // from a payload-less fallback (specs/022).
    let latest: ProductTemplate;
    try {
      latest = await client.getTemplate(full.templateId);
    } catch (e) {
      setError(`Couldn't load "${full.name}" to render — ${e instanceof Error ? e.message : String(e)}`);
      return;
    }
    try {
      // F-7: the editor already holds both catalog lists (the Details selects) — resolve the
      // model/style NAMES through them so the export path, sheet, SKU and chip never show ids.
      addToBatch(selectionFromTemplate(latest, customerId, { phoneModels, caseStyles }));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      return;
    }
    if (typeof window !== "undefined") window.location.hash = "batch";
  }, [addToBatch, client, full.templateId, full.name, phoneModels, caseStyles]);

  // B4: the reference image is per ACTIVE view — read the active draft's `base_case_image_ref`.
  const payloadRef = useMemo(() => {
    const ref = viewDrafts.find((v) => v.view_type === activeView)?.base_case_image_ref;
    return ref ?? undefined;
  }, [viewDrafts, activeView]);
  // The case image: prefer the version payload ref; otherwise fall back to the locally-stored
  // (encrypted on desktop) generated image so it never goes missing. The local fallback is the
  // PRIMARY (back) product image, so it only applies on the primary view.
  // State: `undefined` = still probing the local store; `null` = probed, nothing there — the
  // distinction gates the hosted baked fallback below so it doesn't flash in before a local
  // copy (same bytes, zero network) has had a chance to resolve.
  const [localUrl, setLocalUrl] = useState<string | null | undefined>(undefined);
  useEffect(() => {
    if (payloadRef || activeView !== PRIMARY_VIEW) { setLocalUrl(null); return; }
    setLocalUrl(undefined); // (re)probing the local store
    let url: string | null = null;
    let cancelled = false;
    void loadTemplateImageUrl(engine, full.templateId).then((u) => {
      if (cancelled) { if (u) URL.revokeObjectURL(u); return; }
      url = u;
      setLocalUrl(u);
    });
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [payloadRef, activeView, engine, full.templateId]);
  // LAST-RESORT hosted fallback: the active view's baked `generated` image (the product-on-white
  // PNG the authoring canvas displayed at bake time). Wizard-created templates never persisted a
  // `base_case_image_ref` — the case photo lived ONLY in the authoring device's local store — so
  // every OTHER device/user opened this editor to a BLANK canvas ("template layers missing /
  // coming blank"). The baked refs ARE hosted (spec-019 sync), so they answer here.
  // ED-2 + ED-12: recomputed every render from the LIVE refs (`liveRefs()`), NOT memoized on the
  // mount-time `full.currentVersion.bakedAssets`. A view-tab switch remounts the MaskEditor, and
  // the memo handed it the OPEN-TIME snapshot — so an in-session AI removal / calibration (whose
  // refs live in `lastKnownRefsRef`) was silently dropped on the way back. `liveRefs()` also hides
  // a removed view's refs so a re-added view doesn't revive them.
  const bakedCaseUrl = refsForView(liveRefs(), activeView)?.generated_url || undefined;
  // Prefer a freshly-uploaded reference for the active view (item 5D) so the swap shows instantly.
  // Chain: staged swap → persisted payload ref → local store (authoring device) → hosted baked
  // `generated` (any synced device). While the local probe is still running (`undefined`) show
  // nothing rather than flashing the remote image in front of an imminent local resolve.
  const caseImageUrl = resolveCaseImageUrl({ staged: newRefUrl[activeView], payloadRef, recordLoaded, localUrl, bakedCaseUrl });

  // Camera shading fix (owner, 2026-10-15): the view's print guide takes the original AI image's
  // shading back over each camera island. The fix is saved with the view, so every device and
  // Re-key all apply it too.
  const [listedJobs, setListedJobs] = useState<GenerationJobView[]>([]);
  const [shadingBusy, setShadingBusy] = useState(false);
  const madeByGenerate = Boolean(
    (full.currentVersion.bakedAssets as { shaded_magenta_guide?: boolean } | null | undefined)?.shaded_magenta_guide);
  useEffect(() => {
    if (!madeByGenerate) return;
    let cancelled = false;
    Promise.resolve().then(() => client.listGenerationJobs({ limit: 200 })).then(
      (r) => { if (!cancelled) setListedJobs(r.jobs); },
      () => {}, // Without the list the fix is offered only for jobs this editor applied.
    );
    return () => { cancelled = true; };
  }, [client, full.templateId, madeByGenerate]);
  const activeDraftView = viewDrafts.find((d) => d.view_type === activeView);
  const activeShadingFix = activeDraftView?.shading_fix ?? null;
  const shadingJobId = shadingSourceJob(
    listedJobs, liveGenJobs, full.templateId, activeView,
    primaryView(full.currentVersion.payload)?.view_type ?? PRIMARY_VIEW,
  );
  const setShadingFix = useCallback((fix: WireView["shading_fix"]) => {
    setViewDrafts((prev) => prev.map((d) => (d.view_type === activeView ? { ...d, shading_fix: fix } : d)));
    setSave("idle");
  }, [activeView]);
  const enableShadingFix = useCallback(async () => {
    if (!shadingJobId) return;
    setError(null);
    setShadingBusy(true);
    try {
      // The listed link may have expired: read the job for a fresh one.
      const { originalUrl } = await client.getGenerationJob(shadingJobId);
      if (!originalUrl) throw new Error("The original AI image of this view is no longer available.");
      setShadingFix({ original_ref: await hostGenerated(originalUrl), margin: DEFAULT_ISLAND_MARGIN });
    } catch (e) {
      setError(editorErrorMessage(e, "fix the camera shading"));
    } finally {
      setShadingBusy(false);
    }
  }, [shadingJobId, client, hostGenerated, setShadingFix]);
  const setShadingMargin = useCallback((margin: number) => {
    if (activeShadingFix) setShadingFix({ ...activeShadingFix, margin });
  }, [activeShadingFix, setShadingFix]);
  const setFlatten = useCallback((flatten: boolean) => {
    setViewDrafts((prev) => prev.map((d) => (d.view_type === activeView ? { ...d, flatten_magenta: flatten } : d)));
    setSave("idle");
  }, [activeView]);
  const shaded = useFinishedCaseUrl(caseImageUrl, activeDraftView, logger);
  // The view's clear image (same frame as its print guide) is the preview's backdrop, so an empty
  // print area shows the phone through the clear case. Preview only: nothing is saved.
  const previewBackdropUrl = shadingJobId
    ? ([...listedJobs, ...liveGenJobs.map((e) => e.job)].find((j) => j.id === shadingJobId)?.clearUrl ?? undefined)
    : undefined;

  // Sources (owner, 2026-10-15): every stored image this template was made from, read fresh on
  // open because the jobs' links expire.
  const [sources, setSources] = useState<{ jobs: GenerationJobView[]; loading: boolean } | null>(null);
  const openSources = useCallback(async () => {
    setSources({ jobs: [], loading: true });
    const session = new Set(liveGenJobs.map((e) => e.id));
    try {
      const { jobs } = await client.listGenerationJobs({ limit: 200 });
      const mine = jobs.filter((j) => j.publishedTemplateId === full.templateId
        || j.templateId === full.templateId || session.has(j.id));
      // The list carries no reference links; each job is read again with them.
      const withInputs = await Promise.all(mine.map((j) =>
        client.getGenerationJob(j.id, { inputs: true }).catch(() => j)));
      setSources({ jobs: withInputs, loading: false });
    } catch (e) {
      setSources({ jobs: [], loading: false });
      logger?.log("warn", "Editor · Sources: the Generate jobs could not be read", { error: e });
    }
  }, [client, full.templateId, liveGenJobs, logger]);
  const templateImages: SourceImage[] = [
    ...(typeof payloadRef === "string" ? [{ label: "Case image (saved)", url: payloadRef }] : []),
    ...(activeShadingFix ? [{ label: "Original AI image (shading fix)", url: activeShadingFix.original_ref }] : []),
  ];
  shadingPendingRef.current = shaded.pending;

  // Spec 019 baked-asset cross-device sync. Extracted so BOTH the editor's onBaked and the
  // banner's Retry run the EXACT same upload with the same bytes. Best-effort by design (the
  // local bake already works), but the failure is now SURFACED via `bakedSync` → the retry
  // banner, instead of being swallowed so other devices silently render design-only. This never
  // blocks/fails Save (submitVersion) — it's a separate, independent flow.
  const syncBakedAssets = useCallback(
    ({ assets, maskPng, printMaskPng, mattePng, aiEdge, aiEdgeKeep, edgeFeather, edgeChoke, matteStrength, matteKind, matteCleared, layerRefs }: BakedPayload): Promise<void> => {
      const versionNumber = draft?.versionNumber ?? full.currentVersion.versionNumber;
      lastBakedRef.current = { assets, maskPng, printMaskPng, mattePng, aiEdge, aiEdgeKeep, edgeFeather, edgeChoke, matteStrength, matteKind, matteCleared, layerRefs };
      // B4: bakes happen on the ACTIVE view. `lastKnownRefs` is the version's LIVE multi-view refs
      // map (threaded forward across same-session bakes, NOT the stale mount snapshot); the ACTIVE
      // view's own refs drive matte carry-forward, and the persisted dict is built via
      // `mergeViewRefs` so writing this view never wipes the sibling views.
      const bakeView = activeView;
      const lastKnownRefs = lastKnownRefsRef.current;
      // The ACTIVE view's EXISTING baked refs — threaded so a matte-less re-bake CARRIES the
      // already-hosted matte + AI-edge state forward instead of the cloud full-overwrite wiping it
      // (fixes "AI-remove button resets on reopen while the preview still shows the processed image").
      const previousRefs = refsForView(lastKnownRefs, bakeView);
      setBakedSync("syncing");
      setBakedSyncDismissed(false);
      logger?.breadcrumb("action", "Sync: uploading baked mockup assets to cloud", {
        templateId: full.templateId, versionNumber,
      });
      const run = (async () => {
        // SERIALIZE overlapping bakes: wait for any in-flight sync to SETTLE before persisting
        // this one. Bake view A (upload in flight) → switch → bake view B must not persist B's
        // merge onto a base that predates A's — that full-overwrites and DROPS A's bake. Awaiting
        // the predecessor lets it advance `lastKnownRefsRef.current` first, so the read in `mapRefs`
        // below merges onto A's fresh refs. `.catch` so a FAILED predecessor never blocks this one.
        const prev = pendingSyncRef.current;
        if (prev) await prev.catch(() => {});
        try {
          // The presign/PUT/verify/setBakedAssets core lives in the shared helper (also used by the
          // Wizard's post-submit upload). This wrapper owns only the React state (banner) + the
          // token adoption below — behaviourally identical to the inlined version it replaced.
          // Capture the EXACT merged map we persist so the live snapshot can be advanced to it on
          // success — the next same-session bake merges onto this, not the stale open-time value.
          let persistedMap: BakedAssetRefsMap | null = null;
          const updated = await syncBakedAssetsToCloud(client, logger, {
            templateId: full.templateId,
            versionNumber,
            surface: "editor",
            // WS-P: read the value the app warmed once at sign-in — never fetched here. A
            // network call on the bake path would be a new failure mode in exactly the flow
            // this wave exists to make reliable. `undefined` still attempts (the 409 backstop).
            hostingEnabled: skipHostingPreflightRef.current ? undefined : hostingEnabled(),
            payload: { assets, maskPng, printMaskPng, mattePng, aiEdge, aiEdgeKeep, edgeFeather, edgeChoke, matteStrength, matteKind, matteCleared, previousRefs, layerRefs },
            // B4: merge this view's fresh refs into the existing per-view map so a per-view bake
            // persists ONLY its own view and never wipes the sibling views. For the primary (back)
            // view `mergeViewRefs` also refreshes the legacy flat fields (old builds keep rendering).
            // Read `lastKnownRefsRef.current` at PERSIST time (not the start-captured base): the
            // serialized predecessor above has by now advanced it to include the earlier view's
            // refs, so this merge never clobbers a concurrently-baked sibling view.
            mapRefs: (refs) => {
              persistedMap = mergeViewRefs(lastKnownRefsRef.current, bakeView, refs);
              return persistedMap as unknown as Record<string, unknown>;
            },
          });
          // Persist SUCCEEDED (no throw) → advance the live refs snapshot to exactly what the cloud
          // now holds, so a subsequent bake of ANOTHER view builds its merge onto this view's fresh
          // refs instead of clobbering them.
          if (persistedMap) lastKnownRefsRef.current = persistedMap;
          // setBakedAssets rotates the draft's version_token server-side (optimistic-
          // concurrency bump). The editor USED to discard this return, leaving draft
          // state on the STALE token — so the very next Save (submitVersion sends
          // draft.versionToken) 409'd "this version changed elsewhere" on the user's
          // OWN single-device edit (Preview → Save). Adopt the fresh token so Save
          // succeeds. Guard on versionNumber so a bake that resolves after the user
          // has moved to a different draft can't clobber the newer draft's token.
          // Also record it in latestTokenRef so a Save serialized behind this sync submits with the
          // POST-rotation token (the `draft` closure is stale by then, and a setDraft updater runs
          // too late — Save reads the ref the instant this promise resolves). Guard against a bake
          // that resolves after the user opened a NEWER draft clobbering the newer token.
          if (draftVersionRef.current === updated.versionNumber) {
            latestTokenRef.current = updated.newVersionToken;
          }
          setDraft((d) =>
            d && d.versionNumber === updated.versionNumber
              ? { ...d, versionToken: updated.newVersionToken }
              : d,
          );
          setBakedSyncError(null);
          setBakedSync("ok");
        } catch (err) {
          // Best-effort by design (local render still has the assets), but NEVER silent: a
          // swallowed upload failure is exactly why "the mockup image doesn't show on another
          // computer" was impossible to diagnose. Log it WITH structured detail (step + stack)
          // AND flip to `failed` so the retry banner appears — the admin can re-sync or dismiss.
          logger?.log("error", "Sync: baked-asset cross-device sync FAILED (other devices render design-only)", {
            error: err, step: "uploadBakedAssets", templateId: full.templateId, versionNumber,
          });
          setBakedSyncError(err instanceof Error ? err.message : String(err));
          // WS-P: an upload that CANNOT succeed is a different offer from one that might. The
          // pre-flight throws this before any network work, so there is nothing to retry until
          // a platform admin turns hosting on — and nothing to dismiss, either.
          setBakedSync(err instanceof HostingDisabledError ? "blocked" : "failed");
        }
      })();
      // Publish this as the in-flight sync so `saveDraft` can serialize behind it (the whole chain
      // above, incl. token adoption). Clear on settle, but only if a newer bake hasn't replaced it.
      pendingSyncRef.current = run;
      void run.finally(() => { if (pendingSyncRef.current === run) pendingSyncRef.current = null; });
      return run;
    },
    [client, logger, draft?.versionNumber, draft?.versionToken, full.currentVersion.versionNumber, full.currentVersion.bakedAssets, full.templateId, activeView],
  );

  // Retry the failed baked-asset sync. `bakedSyncRetrying` keeps the banner + its "Loading…"
  // state visible while in flight (a fresh bake sets `syncing` too, but that must NOT flash a
  // banner on every preview — only a retry shows it).
  //
  // BAKE-AWARE (F3 follow-up): when the editor holds UNBAKED dirty state, re-uploading the
  // retained payload would push STALE bytes (a prior preview) and could flip the banner to "ok"
  // while the current state stays unbaked — and after a bakeNow no-emit there may be NO retained
  // payload at all, making Retry a silent dead button. So: dirty → bake FIRST (a successful bake
  // emits a fresh onBaked → fresh syncBakedAssets, superseding any stale payload); not dirty +
  // payload retained → re-upload those exact bytes; neither → warn loudly, keep the banner.
  const retryBakedSync = useCallback(() => {
    if (bakedSyncRetrying) return;
    setBakedSyncError(null); // a stale reason must not sit under a fresh attempt
    // m-1: a Retry from the BLOCKED banner re-asks the cloud rather than the session value.
    skipHostingPreflightRef.current = bakedSync === "blocked";
    setBakedSyncRetrying(true);
    void (async () => {
      try {
        const versionNumber = draft?.versionNumber ?? full.currentVersion.versionNumber;
        if (bakeRef.current?.isDirty()) {
          logger?.breadcrumb("action", "Retry: baking current editor state before re-sync", {
            templateId: full.templateId, versionNumber, path: "bakeNow",
          });
          const baked = await bakeRef.current.bakeNow();
          if (baked) {
            // bakeNow emitted onBaked → a fresh syncBakedAssets is in flight; await it so the
            // banner's "Loading…" holds until the upload actually settles (state set by the sync).
            await pendingSyncRef.current?.catch(() => {});
            return;
          }
          logger?.log("warn", "Retry: forced bake produced no upload — cross-device assets remain stale", {
            templateId: full.templateId, versionNumber, path: "bakeNow-no-emit",
          });
          setBakedSyncError("the forced re-bake produced nothing to upload");
          setBakedSync("failed"); // no false "ok" — keep the banner
          return;
        }
        const payload = lastBakedRef.current;
        if (payload) {
          logger?.breadcrumb("action", "Retry: re-uploading retained baked payload", {
            templateId: full.templateId, versionNumber, path: "retained-payload",
          });
          await syncBakedAssets(payload);
          return;
        }
        logger?.log("warn", "Retry: nothing to re-sync — editor is clean and no baked payload is retained", {
          templateId: full.templateId, versionNumber, path: "nothing-to-retry",
        });
        setBakedSyncError("nothing to re-sync — the editor is clean and no baked assets are held");
        setBakedSync("failed"); // keep the banner rather than faking success
      } finally {
        skipHostingPreflightRef.current = false;
        setBakedSyncRetrying(false);
      }
    })();
  }, [bakedSync, bakedSyncRetrying, syncBakedAssets, logger, draft?.versionNumber, full.currentVersion.versionNumber, full.templateId]);

  /* ── the accordion heads' one-line summaries ────────────────────────────────────────
   * A collapsed section has to answer the question its fields answer, or an accordion trades
   * a long page for a guessing game. Details and Template settings read their own state;
   * the two panels below own their fetches, so they report their counts up (`onCount`). */
  const detailsSummary = [
    categories.find((c) => c.value === detailCategory)?.label,
    (() => { const m = phoneModels.find((p) => p.id === detailModel); return m ? modelLabel({ brand: m.brand, model: m.model }) : null; })(),
    caseStyles.find((c) => c.id === detailStyle)?.label,
    detailBaseType || null,
  ].filter(Boolean).join(" · ") || "not set";
  const settingsSummary = [
    visibility || null,
    ownershipType || null,
    syncPermission || null,
    ownerViewType || null,
  ].filter(Boolean).join(" · ") || "unchanged";

  /* The two lifecycle commits, each buildable loud or quiet.
   *
   * `locked = !draft || draftLocked`, so the states are: no draft (unlock is the only commit),
   * draft open (Publish is the commit), and draft-but-re-locked after a submit — where BOTH
   * buttons belong on screen and exactly one of them is the live commit. `ActionBar` COUNTS the
   * rendered `.btn--primary` and reports a second one through `console.error`, in DEV only
   * (`ui/ActionBar.tsx:79-89`); gate row B4 counts them per surface per viewport in a real
   * browser and fails the build. That pair is what makes "exactly one" a fact rather than a
   * habit — the bar has never thrown, and a layout primitive taking a route out on a designer's
   * mistake would be worse than the mistake (`ui/ActionBar.tsx:14-31`). */
  const unlockButton = (loud: boolean) => (
    <button
      type="button"
      className={loud ? "btn btn--primary" : "btn"}
      onClick={() => void openDraft()}
      disabled={busy}
      data-testid="editor-open-draft"
    >
      {busy ? "Opening…" : "Unlock & edit"}
    </button>
  );
  const saveButton = (loud: boolean) =>
    draft ? (
      <button
        type="button"
        className={loud ? "btn btn--primary" : "btn"}
        onClick={() => void publish()}
        disabled={busy}
        data-testid="editor-save"
      >
        {busy ? "Saving…" : approvalsEnabled ? "Submit for approval" : "Publish"}
      </button>
    ) : null;

  return (
    <section className="templates-editor" data-testid="template-editor">
      <div className="panel-head tpl-editor__head">
        {/* Back leads the header (top-left, where every app puts it) — parked at the far
            right it read as "missing". The title sits beside it as the location label. */}
        <div className="row">
          <button type="button" className="btn btn--back btn--sm" onClick={onClose} data-testid="editor-back">
            <Icon name="arrow-left" size={16}/> Back
          </button>
          <strong className="tpl-editor__title">Edit template</strong>
        </div>
        {!placementOnly && <div className="row row--tight">
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={async () => {
              // ED-3: `refreshFull` overwrites the canvas geometry from the server payload.
              if (!await confirmDiscardUnsaved("Refresh")) return;
              setError(null);
              setWarning(null);
              void refreshFull();
            }}
            data-testid="editor-refresh"
            title="Re-pull this template's latest info from the cloud"
          >
            <Icon name="refresh" size={16}/> Refresh
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            disabled={forceSyncing}
            onClick={async () => {
              // Multi-computer editing: another machine may have re-baked this template
              // minutes ago. Force sync re-pulls the record AND re-downloads every view's
              // baked layers even when a (stale) local copy exists.
              // ED-3: it also REMOUNTS the editor, discarding the removal / sliders / staged
              // layers — so confirm first when there is unsaved work to lose.
              if (!await confirmDiscardUnsaved("Force sync")) return;
              setError(null);
              setWarning(null);
              setForceSyncing(true);
              void refreshFull({ force: true }).finally(() => {
                setForceSyncing(false);
                // Remount the mask editor over the fresh record + assets (state re-restores
                // from the re-downloaded layer refs — sliders, layers, calibration).
                setForceSyncEpoch((n) => n + 1);
              });
            }}
            data-testid="editor-force-sync"
            title="Re-download this template's baked layers from the cloud, replacing any stale local copy (use after editing on another computer)"
          >
            <Icon name="refresh" size={16}/> {forceSyncing ? "Loading…" : "Force sync"}
          </button>
        </div>}
      </div>

      {/* F-5 (b) — invalid geometry is said HERE, at the top of the editor, not only as an
          "Invalid: …" line inside the rail's folded "Print area & cutouts" section. It carries
          the fix, so Publish is never a dead end.

          WS-ERR F-13 — and it renders ABOVE the error banner, because the refused-Publish
          message says "Use “Fix cutouts” above". It used to render below it: the sentence
          pointed the operator past the top of the region it was describing. The fix leads;
          the refusal follows it. */}
      <GeometryProblemBanner
        className="tpl-editor__banner"
        printArea={printArea}
        cutouts={cutouts}
        locked={locked}
        onFix={(pa, cs) => { setPrintArea(pa); setCutouts(cs); setError(null); }}
        onRemoveCutout={(i) => setCutouts(cutouts.filter((_, n) => n !== i))}
      />
      {error && (
        <p className="tpl-editor__error tpl-editor__banner" role="alert" data-testid="editor-error">
          <ErrorNotice detail={error} reference={placementOnly ? activeRunView?.jobId : undefined} />
          {/* Owner rule 2026-09-25: an admin taking run views also reads the full text (the
              stop says what to do); a customer reads the code. */}
          {Boolean(runViews?.length) && !placementOnly && <> <span data-testid="editor-error-detail">{error}</span></>}
        </p>
      )}

      {/* ED-17: notices from an action that otherwise SUCCEEDED. Kept out of `error` so a
          successful Publish never renders a red alert beside its own green "Published" chip. */}
      {warning && (
        <div className="banner banner--warn tpl-editor__banner" role="status" data-testid="editor-warning">
          {placementOnly ? <ErrorNotice detail={warning} advisory /> : warning}
        </div>
      )}

      {/* WS-P / AUD-8 LB-8 (i): the bake CANNOT publish — hosting is off for this workspace, so
          every upload attempt 409s and every other device renders this template design-only
          until a platform admin changes a setting. Same tone and vocabulary as the banner below
          (banner--warn, role="status", sentence case, no exclamation), and the same Retry — but
          NO Dismiss: hiding a state the operator cannot otherwise discover is how this defect
          stayed invisible. It disappears on its own once a bake publishes. */}
      {bakedSync === "blocked" && (
        <div
          /* `tpl-editor__above-canvas`, not `tpl-editor__banner`: WS-P put this banner at
             `order: -1`, one step BELOW the error/warning pair at `-2`, and the existing name
             for that bucket is the one the view strip and the preview slot already use.
             Same computed order as the inline object it replaces — nothing moves. */
          className="banner banner--warn row--between row--wrap tpl-editor__above-canvas"
          role="status"
          data-testid="baked-sync-blocked"
        >
          <span>
            This mock-up exists only on this machine. Cross-device mock-ups are off for this
            workspace, so other devices will render this template design-only.{" "}
            {canAccessAdmin(viewerRole)
              ? "Turn on hosting in Settings → Hosting."
              : "Ask a platform admin to turn on hosting in Settings → Hosting."}
          </span>
          <span className="row row--tight">
            <button
              type="button"
              className="btn btn--sm"
              onClick={retryBakedSync}
              disabled={bakedSyncRetrying}
              data-testid="baked-sync-retry"
            >
              {bakedSyncRetrying ? "Loading…" : "Retry"}
            </button>
          </span>
        </div>
      )}

      {/* Baked-asset sync failure notice (spec 019). Non-blocking + dismissible (role="status",
          NOT a modal): Save already succeeded locally, but the cloud copy didn't upload, so other
          devices would render this template WITHOUT its background removal. Retry re-runs the
          exact same upload with the retained bytes; Dismiss hides it. Reuses the .banner--warn
          tone. Stays visible while a retry is in flight (bakedSyncRetrying) so its button can
          show "Loading…" without a fresh preview bake flashing the banner. */}
      {/* WS-P: `bakedSync !== "blocked"` — a Retry pressed on the blocked banner sets
          `bakedSyncRetrying`, which would otherwise raise THIS banner alongside it and show the
          operator two different sentences about one state. One state, one banner. */}
      {(bakedSync === "failed" || (bakedSyncRetrying && bakedSync !== "blocked")) && !bakedSyncDismissed && (
        <div
          /* Same bucket as the blocked twin forty lines above, and as the inline
             `{ order: -1, justifyContent: "space-between", flexWrap: "wrap" }` WS-P gave BOTH
             banners: without it this one computes `order: 0` and a flex column puts the warning
             below the view strip and the mask canvas — off-screen at 1280×800. */
          className="banner banner--warn row--between row--wrap tpl-editor__above-canvas"
          role="status"
          data-testid="baked-sync-banner"
        >
          <span>
            Saved on this device, but the cloud copy didn't sync — other devices may render
            this template without its background removal.
            {bakedSyncError && !placementOnly && (
              <>
                {" "}
                <span data-testid="baked-sync-reason">Reason: {bakedSyncError}</span>
              </>
            )}
          </span>
          <span className="row row--tight">
            <button
              type="button"
              className="btn btn--sm"
              onClick={retryBakedSync}
              disabled={bakedSyncRetrying}
              data-testid="baked-sync-retry"
            >
              {bakedSyncRetrying ? "Loading…" : "Retry"}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setBakedSyncDismissed(true)}
              data-testid="baked-sync-dismiss"
            >
              Dismiss
            </button>
          </span>
        </div>
      )}

      {!placementOnly && <div className="templates-editor__form card">
        {/* Name + status on ONE line — model/style/base-type aren't repeated (editable in Details). */}
        <div className="row row--end row--tight">
          <div className="field tpl-editor__name-field">
            <label htmlFor="template-name">Name</label>
            <input
              id="template-name"
              className="input"
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setNameSaved(false); }}
              data-testid="editor-name"
            />
          </div>
          {/* Name is METADATA — saveable even on a locked/Active version (like Details),
              so it is not gated by `locked`. Dedicated Save so a rename actually persists. */}
          <button
            type="button"
            /* F-41 / LB-8: a metadata commit, not the lifecycle action. The editor has
               exactly ONE .btn--primary — Publish — and it lives in the action bar. */
            className="btn"
            onClick={() => void saveName()}
            disabled={nameSaving || name.trim() === full.name || !name.trim()}
            data-testid="editor-name-save"
          >
            {nameSaving ? "Saving…" : nameSaved ? <><Icon name="check" size={16}/> Saved</> : "Save"}
          </button>
        </div>

        {/* R12 / rule 8 — the four panels are ONE accordion.

            The page drew `<details className="tpl-editor__section">` four times, each with its
            own `::before` chevron in template-editor.css: R12's first idiom. `ui/Accordion.tsx`
            draws the frame and the marker once, in components.css, for every rail in the
            product — and it opens one section at a time, which is what makes four stacked
            panels a column you can read instead of a page you scroll.

            IMP-9 survives: Details is the section that is open when the editor opens. It is
            the field set an operator checks first, and a collapsed <details> made "which model
            is this?" a click away on every open. Every head carries a one-line summary, so a
            closed section still answers the question its fields answer. */}
        <Accordion
          name="tpl-editor-sections"
          openKey={sectionOpen}
          onOpenChange={setSectionOpen}
          items={[
            /* #4/#6 — edit the template DETAILS (category / model / style / base type).
               Metadata-only, so allowed even on a locked/Active template. */
            { key: "details", head: "Details", meta: sectionSummary(detailsSummary), body: (
        <div className="tpl-editor__section-body" data-testid="editor-details">
          <div className="tpl-editor__settings-grid">
            <div className="field">
              <label htmlFor="detail-category">Category</label>
              <select
                id="detail-category"
                className="input"
                value={detailCategory}
                onChange={(e) => { setDetailCategory(e.target.value); setDetailSaved(false); }}
                data-testid="detail-category"
              >
                <option value="">—</option>
                {categories.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="field">
              <label htmlFor="detail-model">
                Model
                {phoneModelsError && (
                  <InlineRetry onRetry={() => void loadPhoneModels()} testId="detail-model-retry" />
                )}
              </label>
              <InlineCreateRow
                label="Add a new phone model"
                fields={[
                  { key: "brand", placeholder: 'Brand (e.g. "Vivo")', ariaLabel: "New model brand" },
                  { key: "model", placeholder: 'Model (e.g. "X200 Pro")', ariaLabel: "New model name" },
                ]}
                onSubmit={submitNewModel}
                onError={(e) => catalogCreateError(e, "phone model")}
                testIdBase="detail-new-model"
                edit={(() => {
                  const m = phoneModels.find((p) => p.id === detailModel);
                  return m ? { values: { brand: m.brand, model: m.model }, onSubmit: submitEditModel } : null;
                })()}
              >
                {/* WS-B B-2: the option label goes through `modelLabel`. A naive
                    `${brand} ${model}` join read "iQOO iQOO 15 5G" — AUD-6 top-10 #2, one
                    dropdown away from the list that already fixed it. */}
                <CatalogSelect
                  id="detail-model"
                  value={detailModel}
                  onChange={(v) => { setDetailModel(v); setDetailSaved(false); }}
                  testId="detail-model"
                  error={phoneModelsError}
                  options={phoneModels.map((p) => ({ value: p.id, label: modelLabel({ brand: p.brand, model: p.model }) }))}
                />
              </InlineCreateRow>
            </div>
            <div className="field">
              <label htmlFor="detail-style">
                Style
                {caseStylesError && (
                  <InlineRetry onRetry={() => void loadCaseStyles()} testId="detail-style-retry" />
                )}
              </label>
              <InlineCreateRow
                label="Add a new case style"
                fields={[{ key: "label", placeholder: 'New case style (e.g. "Clear TPU")', ariaLabel: "New case style name" }]}
                onSubmit={submitNewStyle}
                onError={(e) => catalogCreateError(e, "case style")}
                testIdBase="detail-new-style"
                edit={(() => {
                  const c = caseStyles.find((x) => x.id === detailStyle);
                  return c ? { values: { label: c.label }, onSubmit: submitEditStyle } : null;
                })()}
              >
                <CatalogSelect
                  id="detail-style"
                  value={detailStyle}
                  onChange={(v) => { setDetailStyle(v); setDetailSaved(false); }}
                  testId="detail-style"
                  error={caseStylesError}
                  options={caseStyles.map((c) => ({ value: c.id, label: c.label }))}
                />
              </InlineCreateRow>
            </div>
            <div className="field">
              <label htmlFor="detail-base-type">Base type</label>
              <InlineCreateRow
                label="Use a custom base type"
                fields={[{
                  key: "value",
                  placeholder: 'Custom base type (e.g. "carbon fiber")',
                  ariaLabel: "Custom base type",
                  // LB-2: `templates.base_type` is String(30). Postgres raises
                  // StringDataRightTruncation → an unexplained 500; SQLite (dev) does not.
                  maxLength: 30,
                }]}
                onSubmit={async (v) => { setDetailBaseType(v.value!); setDetailSaved(false); }}
                onError={(e) => catalogCreateError(e, "base type")}
                testIdBase="detail-new-base-type"
              >
                <select
                  id="detail-base-type"
                  className="input"
                  value={detailBaseType}
                  onChange={(e) => { setDetailBaseType(e.target.value); setDetailSaved(false); }}
                  data-testid="detail-base-type"
                >
                  <option value="">—</option>
                  {BASE_TYPES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                  {detailBaseType && !BASE_TYPES.some((b) => b.value === detailBaseType) && (
                    <option value={detailBaseType}>{detailBaseType}</option>
                  )}
                </select>
              </InlineCreateRow>
            </div>
            <div className="field">
              <label htmlFor="detail-code">Code</label>
              <input
                id="detail-code"
                className="input"
                type="text"
                value={detailCode}
                onChange={(e) => { setDetailCode(e.target.value); setDetailSaved(false); if (detailCodeError) { setDetailCodeError(null); setError(null); } }}
                ref={detailCodeRef}
                aria-invalid={!!detailCodeError || undefined}
                aria-describedby={detailCodeError ? "detail-code-error" : undefined}
                placeholder="Auto-generated if left blank"
                data-testid="detail-code"
              />
              {detailCodeError && <span id="detail-code-error" className="field-error" role="alert">
                <ErrorNotice detail={detailCodeError} />
                <span>Choose a different code, then press Save. Your current edits stay here.</span>
              </span>}
            </div>
          </div>
          <div className="row">
            <button
              type="button"
              className="btn"
              onClick={() => void saveDetails()}
              disabled={detailSaving}
              data-testid="detail-save"
            >
              {detailSaving ? "Saving…" : "Save"}
            </button>
            {detailSaved && <span className="faint" data-testid="detail-saved">Details saved.</span>}
          </div>
        </div>
            ) },
            /* Item 5A: ownership / visibility metadata (setTemplateOwnership). */
            { key: "settings", head: "Template settings", meta: sectionSummary(settingsSummary), body: (
        <div className="tpl-editor__section-body" data-testid="editor-settings">
        <div className="tpl-editor__settings-grid">
          {/* Task 6: per-template background (appearance). Saved via the main Save button
              (render_settings write-through), unlike the ownership selects below (Save settings). */}
          {backgroundGroup && backgroundGroup.choices.length > 0 && (
            <div className="field">
              <label htmlFor="tpl-background">
                {backgroundGroup.label}
                {/* WS-BG: where the Brand color comes from and WHEN it updates. The ⓘ is the
                    explanation channel (design.md Direction B rule 10) — a hint paragraph under
                    the control would be a third one. */}
                <InfoHint
                  text={"Solid color paints the colour you pick. Brand color uses the default brand’s primary colour, "
                    + "re-read each time you Save — changing the brand colour applies to this template on its next Save. "
                    + "Both are painted by the mock-up and the mask render; Transparent empties the canvas except in JPG."}
                  label="About the Background setting"
                />
              </label>
              <select
                id="tpl-background"
                className="input"
                value={background}
                // P0-1: mark the picker TOUCHED so (a) the payload re-seed effect stops following
                // the server value and (b) Save is allowed to write a CLEAR (null). An untouched
                // picker must never be able to delete a stored background.
                onChange={(e) => { backgroundTouchedRef.current = true; setBackground(e.target.value); }}
                data-testid="editor-background"
              >
                <option value="">— None —</option>
                {backgroundGroup.choices.map((c) => (
                  <option key={c.id} value={c.value}>{c.label}</option>
                ))}
              </select>
              {/* WS-BG: "Solid color" needs a colour, so ask for one right here. The swatch is
                  saved with the choice (render_settings.background_color) and is what BOTH
                  render paths paint. */}
              {background === "solid" && (
                <div className="row">
                  <input
                    type="color"
                    id="tpl-background-color"
                    aria-label="Background color"
                    value={backgroundHex}
                    onChange={(e) => { backgroundTouchedRef.current = true; setBackgroundHex(e.target.value); }}
                    data-testid="editor-background-color"
                  />
                  <span className="faint">{backgroundHex}</span>
                </div>
              )}
            </div>
          )}
          <div className="field">
            <label htmlFor="tpl-visibility">Visibility</label>
            <select
              id="tpl-visibility"
              className="input"
              value={visibility}
              onChange={(e) => { setVisibility(e.target.value as TemplateVisibility | ""); setOwnerSaved(false); }}
              data-testid="settings-visibility"
            >
              <option value="">Keep current</option>
              <option value="private">Private</option>
              <option value="shared">Shared</option>
              <option value="hidden">Hidden</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="tpl-ownership">Ownership</label>
            <select
              id="tpl-ownership"
              className="input"
              value={ownershipType}
              onChange={(e) => { setOwnershipType(e.target.value as TemplateOwnershipType | ""); setOwnerSaved(false); }}
              data-testid="settings-ownership"
            >
              <option value="">Keep current</option>
              <option value="private_client">Private client</option>
              <option value="private_owner">Private to its designer</option>
              <option value="shared_platform">Shared platform</option>
              <option value="admin_only">Admin only</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="tpl-sync">Sync permission</label>
            <select
              id="tpl-sync"
              className="input"
              value={syncPermission}
              onChange={(e) => { setSyncPermission(e.target.value as TemplateSyncPermission | ""); setOwnerSaved(false); }}
              data-testid="settings-sync"
            >
              <option value="">Keep current</option>
              <option value="syncable">Syncable</option>
              <option value="non_syncable">Not syncable</option>
            </select>
          </div>
          <div className="field">
            <label htmlFor="tpl-viewtype">View type</label>
            <select
              id="tpl-viewtype"
              className="input"
              value={ownerViewType}
              onChange={(e) => { setOwnerViewType(e.target.value as TemplateViewType | ""); setOwnerSaved(false); }}
              data-testid="settings-viewtype"
            >
              <option value="">Keep current</option>
              <option value="standard">Standard</option>
              <option value="unique">Unique</option>
            </select>
          </div>
        </div>
        <div className="row">
          <button
            type="button"
            className="btn"
            onClick={() => void saveOwnership()}
            disabled={ownerSaving || (!visibility && !ownershipType && !syncPermission && !ownerViewType)}
            data-testid="settings-save"
          >
            {ownerSaving ? "Saving…" : "Save"}
          </button>
          {ownerSaved && (
            <span className="faint" data-testid="settings-saved">Settings saved.</span>
          )}
        </div>
        </div>
            ) },
            /* Item 5B + 5C: version history + review comments. Both panels own their own
               fetch, so both report their count up for the head (`onCount`). */
            { key: "versions", head: "Version history", meta: sectionSummary(countSummary(versionCount, "version")), body: (
        <VersionHistoryPanel
          templateId={full.templateId}
          refreshKey={panelRefresh}
          onChanged={afterLifecycleChange}
          onCount={setVersionCount}
        />
            ) },
            { key: "comments", head: "Review comments", meta: sectionSummary(countSummary(commentCount, "comment")), body: (
        <CommentsThread templateId={full.templateId} refreshKey={panelRefresh} onCount={setCommentCount} />
            ) },
          ]}
        />

        {/* Admin always-editable guarantee: when locked (draft not yet open or
            openDraft failed), show a prominent "Unlock & edit" button that retries
            openDraft(). If there was a failure, the error is shown inline next to
            the button — never a silent dead-end. When unlocked, the normal
            save/reset actions are shown instead. */}

        {/* Task B5: admin generate parity — generate a per-view AI reference right here, then
            apply a finished result as that view's staged reference. Uses the SAME view-pill
            selector (ViewPicker) + poll/queue affordance (useGenerationJobs) as the customer
            Generate flow. Gated on the Views axis group loading (hidden offline/demo). */}
        {viewsAxisGroup && viewsAxisGroup.choices.length > 0 && (
          <div className="tpl-editor__gen-views" data-testid="editor-gen-views">
            <span className="label" title="Upload a reference mock, pick views, and generate a per-view reference image with AI. Apply a finished result as that view’s reference (applied on save).">
              Generate views (AI)
            </span>
            <div className="tpl-editor__ref-strip tpl-editor__gen-strip" data-testid="gen-design-strip">
              {genDesign ? (
                <div className="tpl-editor__ref-tile">
                  <span className="tpl-editor__ref-name muted">{genDesign.name}</span>
                  <button
                    type="button"
                    className="btn btn--sm btn--danger"
                    onClick={() => setGenDesign(undefined)}
                    aria-label={`Remove ${genDesign.name}`}
                    data-testid="gen-design-remove"
                  >
                    <Icon name="close" size={16} />
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  className="dropzone dropzone--add"
                  onClick={() => void pickGenDesign()}
                  data-testid="gen-pick-design"
                >
                  <span className="dropzone__icon" aria-hidden="true"><Icon name="arrow-up" size={16} /></span>
                  <strong>Upload reference</strong>
                </button>
              )}
            </div>
            <ViewPicker
              views={viewsAxisGroup.choices.map((c) => ({ view: c.value, label: c.label }))}
              selected={genSelectedViews}
              max={genMaxViews ?? Infinity}
              onChange={setGenSelectedViews}
            />
            <div className="row row--tight">
              <BusyButton
                /* WS-Z6 / rule 7: the page has ONE primary and it lives in the action bar.
                   Generating a reference is a step inside this panel, not the page's commit. */
                className="btn btn--sm"
                onAction={generateViews}
                disabled={!genDesign || genSelectedViews.length === 0}
                data-testid="gen-generate"
              >
                {genSelectedViews.length > 1 ? `Generate ${genSelectedViews.length} views` : "Generate"}
              </BusyButton>
            </div>
            {liveGenJobs.length > 0 && (
              <ul className="generate-panel__job-rows" data-testid="gen-job-list">
                {liveGenJobs.map((entry, i) => {
                  const j = entry.job;
                  const ready = isReady(j);
                  const b = statusBadge(j.status);
                  return (
                    <li
                      key={entry.id}
                      className="generate-panel__job"
                      data-testid={`gen-job-${i}`}
                      data-status={j.status}
                    >
                      <span className="generate-panel__job-label">{VIEW_LABEL[entry.view] ?? entry.view}</span>
                      <span className={`badge badge--plain badge--${b.variant}`}>{statusLabel(j.status)}</span>
                      {j.status === "clear_review" && (
                        <RowAction label={`Check the clear image: ${VIEW_LABEL[entry.view] ?? entry.view}`}
                          icon="search" onClick={() => setGenReviewId(entry.id)} data-testid={`gen-check-clear-${i}`} />
                      )}
                      {/* WS-Z9 / F-3: a per-row action in a list — found by widening G-60 to
                          `<li>` rows, and the one surface nobody had named. `BusyButton`
                          rather than `RowAction` for the same reason "Use for batch" is: it
                          owns a double-press guard, and applying a reference twice re-uploads
                          it. Same three attributes by hand; the busy state is the spinner. */}
                      {ready && j.resultUrl && (
                        <Tooltip text="Use this generated view as the reference image">
                          <BusyButton
                            className="btn btn--sm btn--icon"
                            aria-label="Apply as reference"
                            busyLabel={<span className="btn-spinner" aria-hidden="true" />}
                            onAction={() => applyAsReference(entry.view, j.resultUrl as string)}
                            data-testid={`gen-apply-${entry.view}`}
                          >
                            <Icon name="image" size={16} />
                          </BusyButton>
                        </Tooltip>
                      )}
                      {(j.status === "failed" || j.status === "held") && (
                        <span className="tpl-editor__job-error" data-testid={`gen-job-error-${i}`}>
                          <ErrorNotice detail={j.error ?? j.status} reference={j.id} />
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {genReviewEntry?.job.status === "clear_review" && (
              <ClearReview job={genReviewEntry.job} label={VIEW_LABEL[genReviewEntry.view] ?? genReviewEntry.view}
                admin={isAdminViewer} onClose={() => setGenReviewId(null)}
                onDecided={() => { setGenReviewId(null); retryGenPoll(); }} onRefused={retryGenPoll} />
            )}
            {/* LB-4(b): the poll gave up (the interval is stopped). Without this the rows sat on
                "Queued" indefinitely with no error and no recovery. Same affordance as the
                catalog-fetch failures above. */}
            {genPollError && (
              <div className="row row--tight" data-testid="gen-poll-retry-row">
                <InlineRetry message={genPollError} label="Retry" testId="gen-poll-retry" onRetry={retryGenPoll} />
              </div>
            )}
            {genAppliedView && (
              <span className="faint" data-testid="gen-applied">
                Applied as {VIEW_LABEL[genAppliedView] ?? genAppliedView} reference — it’s applied on save.
              </span>
            )}
          </div>
        )}
      </div>}

      {/* Preview floated into the FIRST card slot (just under the header) via flex order,
          so the live composite is the first thing you see — without reordering the DOM. */}
      {/* versionId: key the local bake by the DRAFT's version id when a draft is open (a
          re-edit creates a new version). Using full.currentVersion.versionId — the OLD
          Active version — stored the bake under a stale id, so getMockup(newActiveId)
          missed it and the just-baked template rendered design-only. Falls back to the
          active id when no draft is open. */}
      {/* Case-appearance option lists (texture / clear-case background) feed the MaskEditor's
          own selects; when their fetch fails those selects can't populate, so surface a retry
          here rather than leaving them silently absent. Retries only the failed list(s). */}
      {(textureError || clearBgError) && !placementOnly && (
        <div className="row row--tight tpl-editor__above-canvas" data-testid="appearance-retry-row">
          <InlineRetry
            message="Couldn't load case appearance options (texture / clear-case background)."
            label="Retry"
            testId="appearance-retry"
            onRetry={() => {
              if (textureError) void loadTextures();
              if (clearBgError) void loadClearBg();
            }}
          />
        </div>
      )}

      {/* B4: per-view authoring strip — one tab per draft view, "+ Add view" for the rest, and a
          remove control on each (except when only one view remains). */}
      <div className="templates-editor__view-strip" data-testid="view-strip">
        {/* F-74: this is NOT a second tab bar. The template editor already sits under the
            Editor's own tab bar, and a strip rendered in the same `.workspace__tabs`
            vocabulary made two levels of navigation look like one. It is a control on a page
            — a segmented control — so it says so: `role="group"` with `aria-pressed` buttons.

            That also makes the markup legal. As a `role="tablist"` it never was: its element
            children were `<span class="row">` wrappers, not `role="tab"` (AUD-2 IM-7's first
            defect), and there was no tabpanel to control. A `role="group"` may contain
            anything, so the wrappers that carry each view's remove control are fine.

            Every data-testid is unchanged: view-strip, view-tab-<type>, view-remove-<type>,
            view-add. Six editor suites drive them. */}
        <div className="seg" role="group" aria-label="Template view">
          {viewDrafts.map((v) => (
            <span key={v.view_type} className="templates-editor__view-item">
              <button
                type="button"
                aria-pressed={v.view_type === activeView}
                className={`seg__btn${v.view_type === activeView ? " seg__btn--on" : ""}`}
                onClick={() => switchView(v.view_type)}
                data-testid={`view-tab-${v.view_type}`}
              >
                {/* "view" suffix: a lone control reading "Back" sits exactly where a
                    navigation Back button would — operators read it as one. */}
                {VIEW_LABEL[v.view_type]} view
              </button>
              {/* The primary (back) view is never removable — it owns the legacy flat baked_assets
                  fields and is the de-facto primary. Every other view shows a remove control (once
                  more than one view exists). */}
              {/* WS-Z9 / F-15: a `RowAction`, on the map like every other Remove.
                  It drew `close` where rule 13 gives Remove `minus-circle`, and it named
                  itself with a native `title` — a ~1s delay, no styling, nothing at all on a
                  touch device. No `hint`: the `title` it carried was the same string as its
                  accessible name, so there is no sentence to restore.
                  It is a `RowAction` rather than a hand-rolled `btn--icon` because the map
                  check reads hand-rolled icon buttons only inside a `<td>`/`<li>` row region,
                  and these chips sit in a `<span>` — a `RowAction` is checked wherever it is. */}
              {viewDrafts.length > 1 && v.view_type !== PRIMARY_VIEW && !placementOnly && (
                <RowAction
                  label={`Remove ${VIEW_LABEL[v.view_type]} view`}
                  icon="minus-circle"
                  tone="quiet"
                  onClick={() => removeView(v.view_type)}
                  data-testid={`view-remove-${v.view_type}`}
                />
              )}
            </span>
          ))}
        </div>
        {addableViews.length > 0 && !placementOnly && (
          /* Controlled, and reset through state. It used to write `e.target.value = ""`
             directly after calling addView — a DOM mutation React does not know about, so the
             next render could disagree with the box on screen (AUD-2 IM-7's third defect). */
          <select
            className="input templates-editor__view-add"
            value={ADD_VIEW_PLACEHOLDER}
            onChange={(e) => {
              const next = e.target.value;
              if (next !== ADD_VIEW_PLACEHOLDER) addView(next);
            }}
            data-testid="view-add"
            aria-label="Add view"
          >
            <option value={ADD_VIEW_PLACEHOLDER}>+ Add view</option>
            {addableViews.map((v) => <option key={v} value={v}>{VIEW_LABEL[v]}</option>)}
          </select>
        )}
        {/* WS-Z6 — the template's STATE belongs on the line that says which view you are
            editing, not buried in the metadata form below the canvas. This is the page's mode
            line (rule 5's neighbour: `.seg` stays a segmented control for modes), and the pill
            is the first thing an operator wants beside it: "which view, and is it live?".

            WS-B B-2: the SHARED pill rules, so this template describes itself the same way
            here as it does in the row that opened it. It was hardcoded `badge--neutral` with
            the raw status, so a Pending template read "Pending approval" + amber in the list
            and "Pending" + grey here. */}
        <span
          className={`badge badge--plain tpl-editor__view-status ${templateStatusBadgeClass(status)}`}
          data-testid="editor-status"
        >
          {templateStatusLabel(status)}
        </span>
        {!placementOnly && (
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => void openSources()}
            title="Every image this template was made from" data-testid="editor-sources">
            Sources
          </button>
        )}
        {sources && (
          <SourcesDialog jobs={sources.jobs} templateImages={templateImages} loading={sources.loading}
            onClose={() => setSources(null)} />
        )}
      </div>

      <div className="templates-editor__preview-slot tpl-editor__above-canvas">
      <MaskEditor
        // F-35 / AUD-2 IM-2: `activeView` is NOT in this key. It used to be, so every view-tab
        // switch remounted the editor and threw away the ~109 MB segmentation model, the undo
        // history, the zoom, the loaded test design and the decoded source. The editor takes
        // `view` as a PROP and resets exactly the per-view state (masks, quads, matte, marker
        // cache, layer refs, history seed) through `resetForView` — see `editor/useMatte.ts`,
        // `editor/useLayerAssets.ts` and `editor/useCaseImageSource.ts`.
        // `forceSyncEpoch` and `prunedEpoch` STAY: both mean "the server payload underneath you
        // changed", and ED-12's tombstone comment says outright that a remount is the mechanism.
        // They no longer cost the model — that now lives in `editor/segmentationModel.ts`.
        key={`${draft?.versionId ?? full.currentVersion.versionId}:${forceSyncEpoch}:${prunedEpoch}`}
        engine={engine}
        logger={logger}
        bakeRef={bakeRef}
        caseImageUrl={shaded.url}
        previewBackdropUrl={previewBackdropUrl}
        cameraShading={(
          <CameraShadingFix
            locked={locked}
            available={Boolean(shadingJobId)}
            fix={activeShadingFix}
            flatten={Boolean(activeDraftView?.flatten_magenta)}
            onFlatten={setFlatten}
            busy={shadingBusy}
            onEnable={() => void enableShadingFix()}
            onMargin={setShadingMargin}
            onRemove={() => setShadingFix(null)}
          />
        )}
        printArea={printArea}
        cutouts={cutouts}
        onPrintAreaChange={onPrintAreaEdited}
        onCutoutsChange={onCutoutsEdited}
        // P2-3: the brand-logo quad is now EDITABLE here (drag the amber box), not wizard-only.
        brandArea={brandArea}
        onBrandAreaChange={onBrandAreaEdited}
        onNaturalDims={setBaseDims}
        // WS-EXIF: the frame this view's geometry was drawn on, and the operator's answer when it
        // does not match the photo in front of them.
        authoringFrame={activeAuthoringFrame}
        onAuthoringFrameResolved={onAuthoringFrameResolved}
        baseType={detailBaseType || full.baseType}
        versionId={draft?.versionId ?? full.currentVersion.versionId}
        view={activeView}
        // F-5: the rail's open section, held above the `key` above so the remount cannot take it.
        railOpenKey={maskRailOpen}
        onRailOpenChange={setMaskRailOpen}
        templateId={full.templateId}
        // WS-BG F2: the live preview composites on the template's chosen background.
        templateBackgroundColor={previewBackgroundColor}
        textureOptions={textureOptions}
        clearBgOptions={clearBgOptions}
        onUploadImage={async (bytes) => {
          // Push a CUSTOM (non-library) layer image to hosting (R2) so it is never kept
          // device-local — the layer then syncs to every system like a library pick.
          try {
            const { upload_url, public_url, fields } = await client.templateReferenceUpload(full.templateId, "png");
            if (upload_url.startsWith("file://")) {
              // Was a SILENT null — the operator had no idea the layer never left this device.
              logger?.log("warn", "custom layer upload skipped — hosting is local-only (file:// target), the layer cannot sync to other devices");
              return null;
            }
            const res = await fetchWithTimeout(upload_url, { method: "PUT", headers: { ...(fields ?? {}) }, body: bytes as BodyInit },
              UPLOAD_TIMEOUT_MS, "Case photo upload");
            if (!res.ok) throw new Error(`layer upload PUT -> ${res.status}`);
            if (!isDurableRef(public_url)) {
              // A PUT can succeed while the public URL is root-relative (empty hosting
              // public host) — persisting it would 404 on every device. Refuse it here.
              throw new Error(`hosting returned a non-durable public URL (${String(public_url).slice(0, 48)}) — check hosting_public_host`);
            }
            return public_url;
          } catch (err) {
            logger?.log("error", `custom layer upload to R2 failed — ${err instanceof Error ? err.message : String(err)}`);
            return null;
          }
        }}
        locked={locked}
        onCreateNewVersion={() => void openDraft()}
        initialLayerRefs={(() => {
          // B4: restore the ACTIVE view's layer refs. The PRIMARY (back) view reads the whole flat
          // baked_assets object (so a legacy `{}` still yields defined refs — Task 10 semantics);
          // a non-primary view reads its `views[activeView]` map entry (null → brand-new-view default).
          // ED-2 + ED-12: the LIVE refs (same reasoning as `bakedCaseUrl` above).
          const allRefs = liveRefs();
          const b = activeView === PRIMARY_VIEW ? allRefs : (allRefs?.views?.[activeView] ?? null);
          // Pass refs whenever bakedAssets exists at all — even an all-empty/legacy object — so the
          // MaskEditor restore effect runs and re-seeds `fillFit` via `refs.fillFit ?? "height"`.
          // Gating on individual fields (old behavior) left a plain legacy template (no texture, no
          // clear-bg, rotation 0 — falsy, never persisted — no matte, no fill, no aspect) with
          // `initialLayerRefs === undefined`, which MaskEditor treats as a BRAND-NEW template and
          // keeps its "stretch" default — the next Save then stamped `design_fill:"stretch"` onto a
          // template that must keep rendering as "height". See Task 10 review finding.
          //
          // The snake_case → camelCase FIELD MAPPING itself lives in `./layerRefs.ts` — extracted
          // because inline here it was untestable (anonymous IIFE in a JSX prop, and the one suite
          // that mounts this component mocks MaskEditor away) and a dropped field was invisible.
          // There it is a `Record`-keyed table, so omitting a `BakedAssetRefs` field is a tsc error.
          return b ? refsToInitialLayerRefs(b) : undefined;
        })()}
        // INVARIANT: TemplateEditor must always pass `versionId` alongside `onBaked`. The bake gate
        // is `versionId || onBaked`; emit-only mode (onBaked without a versionId) is wizard-only.
        onBaked={(payload) => {
          bakedRunViewsRef.current.add(activeView);
          void syncBakedAssets(payload);
        }}
        // A Generate run's view keys its print guide like the wizard keys a view, on the
        // wizard's rails: placement for a customer, basic for an admin.
        railMode={runViews?.length ? (placementOnly ? "placement" : "basic") : undefined}
        autoMagentaKey={Boolean(activeRunView)}
        onAutoMagentaKey={activeRunView ? (verdict) => onRunViewKey(activeView, verdict) : undefined}
        shadedMagentaGuide={activeRunView ? true : undefined}
        initialPrintLight={activeRunView ? "reference" : undefined}
        coverageHeightPct={activeRunView?.productHeightPct}
        clearCaseImage={activeRunView?.clear}
      />
      {/* P2-3: add / remove the brand-logo box for the ACTIVE view. Mirrors the wizard's control —
          without it the amber quad could only ever be created at wizard time and never revised. */}
      <label className="field--check" data-testid="editor-brand-logo">
        <input
          type="checkbox"
          checked={brandArea !== null}
          disabled={locked}
          onChange={(e) => toggleBrandArea(e.target.checked)}
        />
        <span>
          Add a brand-logo area for <strong>{VIEW_LABEL[activeView] ?? activeView}</strong> — drag the
          amber box to position where an approved brand’s logo is merged on Export. (Defaults to a
          200×100px box in the centre.) Saved with this view on Save.
        </span>
      </label>
      </div>{/* /preview slot */}
        {/* F-41 / AUD-6 IMP-10 → Direction B rule 7 — THE ACTION BAR.
            AUD-6 measured the lifecycle controls at 1440×900 on first load of the mask editor:
            Publish y=1341, Reset 1341, "Use this template for batch" 1341 — none of them in the
            viewport. An operator opened a template and saw a canvas with no way to save or
            publish it until they scrolled past the canvas. F-41 answered that with a private
            `.tpl-editor__actionbar`; WS-Z6 hands the job to the SHARED `ui/ActionBar.tsx`, one
            of the four bars rule 7 replaces, and template-editor.css keeps it sticky inside
            the card (sticky, not fixed: it never covers the canvas at rest and never escapes).

            The bar takes the primary as a PROP, and it counts it. This page used to render two
            `.btn--primary` at once — `locked` is `!draft || draftLocked`, so a draft that has
            already been submitted showed "Unlock & edit" beside "Publish", both loud. Neither
            button is removed; the LOUD one is whichever is the live commit in the state the
            editor is actually in, and the other is a plain `.btn` that still does its job. */}
        <ActionBar
          label="Template actions"
          primary={locked ? unlockButton(true) : saveButton(true)}
        >
          {locked && unlockError && (
            <span className="row templates-editor__actions" data-testid="editor-unlock-row">
              <span className="tpl-editor__unlock-error" role="alert" data-testid="editor-unlock-error">
                <ErrorNotice detail={unlockError} />
              </span>
            </span>
          )}

          {/* All template actions on ONE wrapping row — save/reset, use-for-batch, and the
              secondary "replace reference image" — so the card stays compact. */}
          <span className="row templates-editor__actions" data-testid="editor-ref-replace">
            {/* A submitted draft re-locks itself, so BOTH commits can be on screen. The one
                that is not the live commit renders quiet rather than disappearing. */}
            {locked && draft && saveButton(false)}
            {draft && !placementOnly && (
              <button type="button" className="btn" onClick={reset} disabled={busy} data-testid="editor-reset">
                Reset
              </button>
            )}
            {!placementOnly && <>
            {/* `.btn--success` retired (F-81): a fourth filled tier competing with primary for
                the One Voice Rule's single loud invitation. "In batch" is a done state, said by
                the check icon in the label, not by a fill. */}
            <button
              type="button"
              className="btn"
              onClick={() => void sendToBatch()}
              data-testid="editor-use-for-batch"
            >
              {inBatch ? <><Icon name="check" size={16}/> In batch — open Batch</> : "Use this template for batch"}
            </button>
            {/* Item 5D: replace the reference (case) image — presigns + PUTs, applied on next save. */}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="is-hidden"
              disabled={locked || refUploading}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void replaceReference(f);
              }}
              data-testid="editor-ref-input"
            />
            <button
              type="button"
              className="btn btn--sm"
              disabled={locked || refUploading}
              onClick={() => fileInputRef.current?.click()}
              data-testid="editor-ref-button"
            >
              {refUploading ? "Uploading…" : "Replace reference image"}
            </button>
            </>}
            {save === "saved" && savedStatus && (
              <span className="faint" data-testid="editor-saved">
                <span className={saveStatusCopy(savedStatus).chipClass} data-testid="editor-saved-chip">
                  {saveStatusCopy(savedStatus).chipLabel}
                </span>{" "}
                {saveStatusCopy(savedStatus).headline}
              </span>
            )}
            {Object.values(newRefUrl).some(Boolean) && !placementOnly && (
              <span className="faint tpl-editor__ref-status" data-testid="editor-ref-ready">
                New reference ready — it’s applied on save.
              </span>
            )}
          </span>
        </ActionBar>
    </section>
  );
}
